import { useState, useRef, useCallback, useEffect, useMemo, lazy, Suspense } from 'react'
import { loadImageToCanvas, invertRgbCanvas, isLikelyImageFile, imageToOutlines, normalizeOutlineCanvasPolarity, outlineToOverlay, outlineToFillBarrier, createCharacterCanvas, createAbcCanvas, createHebrewAlphabetCanvas, createWordCanvas, createTemplateShapeCanvas, createGeometricOutlineCanvas, DEFAULT_MAX_IMAGE_LOAD, IMAGE_TO_OUTLINES_LINE_ART, TEMPLATE_MAX_IMAGE_LOAD, whitenColoringTemplatePaperInPlace } from './utils/imageProcessing'
import { getDeeplabObjectMaskBinary } from './utils/deeplabMask'
import { photoToMandalaCanvas } from './utils/mandalaTransform'
import { ColoringCanvas, fillCanvasPaperBackground } from './ColoringCanvas'
import { getStyledColor } from './utils/colorStyles'
// Icons loaded before MainTools so tool icons (e.g. IconFill) are initialized before use
import { IconClose, IconMenu, IconHome, IconEye, IconUndo, IconRedo, IconPlusSparkles, IconSpeaker, IconBearFlag, IconUser } from './components/Icons'
import { MainTools } from './MainTools'
import { ToolSizeControl } from './ToolSizeControl'
import { SimpleColorPalette } from './SimpleColorPalette'
import { MenuPanel } from './MenuPanel'
import { ColorPalette } from './ColorPalette'
import { PaintStyleBar } from './PaintStyleBar'
import { Gallery } from './Gallery'
import { getGallery, addToGallery, upsertAutosave, GALLERY_AUTOSAVE_ID } from './utils/galleryStorage'
import { createRoom, joinRoom, getEvents, postEvent, auditDevice, isCapacitorNativeProd, isCapacitorNativeApp } from './utils/roomApi'
import { getTextToSpeak, speakHebrew, cancelSpeech } from './utils/speech'
import { ToastContainer } from './components/Toast'
import { ExportModal } from './ExportModal'
import { FlowEditorModal } from './components/FlowEditorModal'
import { FunPanel } from './components/FunPanel'
import { GeometricBuilder } from './components/GeometricBuilder'
import { ConfettiBurst } from './components/ConfettiBurst'
import { SparkleBurst } from './components/SparkleBurst'
import { PaintSplashBurst } from './components/PaintSplashBurst'
import { CanvasViewport } from './CanvasViewport'
import './App.css'
import { getInitialLang, saveLang, t as tr } from './i18n'
import { Capacitor } from '@capacitor/core'
import { pickFromCameraOnly } from './utils/nativeImagePick'

const TemplatesGallery = lazy(() => import('./components/TemplatesGallery').then(m => ({ default: m.TemplatesGallery })))
const AdminCategories = lazy(() => import('./components/AdminCategories').then(m => ({ default: m.AdminCategories })))

const COLORDEV_SIDE_LOGO_DATA_URL =
  'data:image/png;base64,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'

const DEFAULT_COLORS = [
  '#ff6b6b', '#feca57', '#48dbfb', '#ff9ff3', '#54a0ff',
  '#5f27cd', '#00d2d3', '#ff9f43', '#ee5a24', '#1dd1a1',
  '#2ecc71', '#e056fd', '#ffffff', '#95a5a6', '#2c3e50', '#000000',
]

const SPECIAL_COLORS = [
  '#ffd700', '#c0c0c0', '#cd7f32', '#ff69b4', '#00ffff',
  '#ff1493', '#7fff00', '#ff4500', '#da70d6', '#e6e6fa',
]

const HEBREW_LETTERS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ', 'ק', 'ר', 'ש', 'ת']

/** ערך תקין ל-input type=color (#RRGGBB) */
function hexForNativeColorInput(hex) {
  const h = String(hex ?? '').trim()
  if (/^#[0-9A-Fa-f]{6}$/.test(h)) return h
  if (/^#[0-9A-Fa-f]{3}$/.test(h)) {
    const a = h.slice(1)
    return `#${a[0]}${a[0]}${a[1]}${a[1]}${a[2]}${a[2]}`
  }
  return '#ff6b6b'
}

function isVeryLightHex(hex) {
  const h = String(hex || '').trim()
  if (!/^#[0-9A-Fa-f]{6}$/.test(h)) return false
  const r = parseInt(h.slice(1, 3), 16)
  const g = parseInt(h.slice(3, 5), 16)
  const b = parseInt(h.slice(5, 7), 16)
  const lum = (r * 299 + g * 587 + b * 114) / 1000
  return lum >= 236
}

function formatEventTime(createdAt) {
  if (!createdAt) return 'עכשיו'
  const t = new Date(createdAt).getTime()
  const sec = Math.floor((Date.now() - t) / 1000)
  if (sec < 5) return 'עכשיו'
  if (sec < 60) return `לפני ${sec} שניות`
  const min = Math.floor(sec / 60)
  if (min < 60) return `לפני ${min} דק׳`
  return `לפני ${Math.floor(min / 60)} שעות`
}

function getPublicAppBaseUrl() {
  const configured = (import.meta.env.VITE_PUBLIC_APP_BASE_URL || '').trim()
  if (configured) return configured.replace(/\/?$/, '/')

  if (isCapacitorNativeProd()) return 'https://cards.colordev.art/colorphoto/'
  return `${window.location.origin}${(import.meta.env.BASE_URL || '/').replace(/\/?$/, '/')}`
}

export default function App() {
  /** When true: no remote APIs, search, rooms, or external share flows (local-first). Persisted. */
  /** Default: network features on. Set localStorage colorphoto_offline_strict to "1" for strict offline. */
  const [offlineStrictMode, setOfflineStrictMode] = useState(() => {
    if (typeof localStorage === 'undefined') return false
    return localStorage.getItem('colorphoto_offline_strict') === '1'
  })
  const [overlayImage, setOverlayImage] = useState(null) // קווי מתאר בלבד – שחור על שקוף (תמיד מעל)
  const [outlineSize, setOutlineSize] = useState({ w: 0, h: 0 })
  const [isBlankPage, setIsBlankPage] = useState(false) // דף ריק – ציור חופשי בלי תמונה
  const outlineDataRef = useRef(null) // נתוני קווי מתאר למילוי (גבולות)
  const [colors, setColors] = useState([...DEFAULT_COLORS, ...SPECIAL_COLORS])
  const [currentColor, setCurrentColor] = useState('#ff6b6b')
  const [brushSize, setBrushSize] = useState(12)
  /** תרשים זרימה בסגנון Excalidraw — רק עם כלי flow */
  const [flowShape, setFlowShape] = useState('arrow')
  const [tool, setToolState] = useState('brush') // brush | … | splash (דפדפן) | stamp | sticker | flow
  const setTool = useCallback((t) => {
    setToolState(t)
    if (t !== 'sticker') setSelectedSticker(null)
  }, [])
  const [paintStyle, setPaintStyle] = useState('normal') // normal | matte | gold | pastel | metallic | neon
  const [undoStack, setUndoStack] = useState([])
  const [redoStack, setRedoStack] = useState([])
  const [glitterIntensity, setGlitterIntensity] = useState(0.4)
  /** סף בהירות לזיהוי "דף לבן" במילוי רקע (מומחה: נשלט בסליידר) */
  const [paperFillThreshold, setPaperFillThreshold] = useState(248)
  const [stampChar, setStampChar] = useState('')
  const [stampFontFamily, setStampFontFamily] = useState("'Heebo', -apple-system, system-ui, sans-serif")
  const [selectedSticker, setSelectedSticker] = useState(null)
  const [effectTrigger, setEffectTrigger] = useState(null) // { key, type: 'confetti'|'sparkle' } | null
  /** אפקט ספלאש מסך בזריקת צבע (דפדפן בלבד) */
  const [paintSplashFx, setPaintSplashFx] = useState(null) // { key, x, y, color } | null
  const [accessibilityMode, setAccessibilityMode] = useState(false)
  const [readAloudMode, setReadAloudMode] = useState(false)
  const [lang, setLang] = useState(() => (typeof localStorage === 'undefined' ? 'he' : getInitialLang()))
  const [languageGateOpen, setLanguageGateOpen] = useState(() => {
    if (typeof localStorage === 'undefined') return false
    try {
      return !localStorage.getItem('colorphoto_lang')
    } catch {
      return false
    }
  })
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof localStorage === 'undefined') return false
    return localStorage.getItem('colorphoto_dark_mode') === '1'
  })
  const [aboutOpen, setAboutOpen] = useState(false)
  const [adminOpen, setAdminOpen] = useState(false)
  const [qrModalOpen, setQrModalOpen] = useState(false)
  const [templatesGalleryOpen, setTemplatesGalleryOpen] = useState(false)
  const [geoBuilderOpen, setGeoBuilderOpen] = useState(false)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [galleryItems, setGalleryItems] = useState([])
  const [roomId, setRoomId] = useState(null)
  const [roomInviteLink, setRoomInviteLink] = useState('')
  const [roomCreating, setRoomCreating] = useState(false)
  const [roomModalOpen, setRoomModalOpen] = useState(false)
  const [restoreSnapshotUrl, setRestoreSnapshotUrl] = useState(null)
  const [eraserBaseImageUrl, setEraserBaseImageUrl] = useState(null)
  const [welcomeMenuOpen, setWelcomeMenuOpen] = useState(false)
  const [participantName, setParticipantName] = useState(() => typeof localStorage !== 'undefined' ? (localStorage.getItem('coloring_participant_name') || '') : '')
  const [liveUpdateFrom, setLiveUpdateFrom] = useState(null)
  const [roomLiveEvents, setRoomLiveEvents] = useState([])
  const [uniqueDeviceCount, setUniqueDeviceCount] = useState(null)
  const [composeWord, setComposeWord] = useState('')
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [flowEditorModalOpen, setFlowEditorModalOpen] = useState(false)
  const [videoRecording, setVideoRecording] = useState(false)
  const [expertMode, setExpertMode] = useState(false)
  const [splitMode, setSplitMode] = useState(false)
  const [splitRefImageUrl, setSplitRefImageUrl] = useState(null)
  const [canvasZoom, setCanvasZoom] = useState(1)
  const [canvasPan, setCanvasPan] = useState({ x: 0, y: 0 })
  const [gridCells, setGridCells] = useState(null) // null | { rows, cols } — משבצות על הדף (2×2, 3×3 וכו')
  const [triangleOverlay, setTriangleOverlay] = useState(false) // רשת משולשים על הדף
  const [outlineMode, setOutlineMode] = useState(() => {
    try {
      const v = typeof localStorage !== 'undefined' ? localStorage.getItem('colorphoto_outline_mode') : null
      return v === 'drawing' ? 'drawing' : 'lines'
    } catch {
      return 'lines'
    }
  })
  const [outlineQuality, setOutlineQuality] = useState(() => {
    try {
      const v = typeof localStorage !== 'undefined' ? localStorage.getItem('colorphoto_outline_quality') : null
      return (v === 'perfect' || v === 'clean' || v === 'mandala' || v === 'sharp' || v === 'standard' || v === 'soft' || v === 'upscale') ? v : 'perfect'
    } catch {
      return 'perfect'
    }
  })
  const [toolsSideOpen, setToolsSideOpen] = useState(() => {
    if (typeof window === 'undefined') return true
    return window.matchMedia('(min-width: 900px)').matches
  })
  const colorsPinned = true
  const [sensitiveColorPress, setSensitiveColorPress] = useState(() => {
    if (typeof localStorage === 'undefined') return false
    return localStorage.getItem('colorphoto_sensitive_color_press') === '1'
  })
  const [blankPageSize, setBlankPageSize] = useState({ w: 800, h: 600 })
  const [userMode, setUserMode] = useState(() => {
    if (typeof localStorage === 'undefined') return null
    const v = localStorage.getItem('colorphoto_user_mode')
    return v === 'child' || v === 'companion' || v === 'adult' ? v : null
  })
  const childMode = userMode === 'child'
  const adultMode = userMode === 'adult'
  const effectiveExpertMode = childMode ? false : (expertMode || adultMode)
  const [lineAssistEnabled, setLineAssistEnabled] = useState(() => {
    if (typeof localStorage === 'undefined') return false
    return localStorage.getItem('colorphoto_line_assist_enabled') === '1'
  })
  const [lineAssistStrength, setLineAssistStrength] = useState(() => {
    if (typeof localStorage === 'undefined') return 0.72
    const raw = Number(localStorage.getItem('colorphoto_line_assist_strength') || 0.72)
    return Number.isFinite(raw) ? Math.min(0.92, Math.max(0.1, raw)) : 0.72
  })
  const [measurementCalibrationCm, setMeasurementCalibrationCm] = useState(10)
  const [measurementCalibrationPx, setMeasurementCalibrationPx] = useState(100)
  const [measurementPoints, setMeasurementPoints] = useState([])
  const [projectNotes, setProjectNotes] = useState('')
  const [adultPreset, setAdultPreset] = useState('tattoo')
  const webPaintSplash = useMemo(() => !isCapacitorNativeApp(), [])

  useEffect(() => {
    if (!webPaintSplash && tool === 'splash') setTool('brush')
  }, [webPaintSplash, tool, setTool])

  useEffect(() => {
    if (!effectiveExpertMode) {
      setCanvasZoom(1)
      setCanvasPan({ x: 0, y: 0 })
    }
  }, [effectiveExpertMode])
  const [mainMenuOverlayOpen, setMainMenuOverlayOpen] = useState(false)
  // When any overlay/menu is open, the right-side tools/colors drawer shouldn't capture taps.
  // Otherwise it can block opening modals like "Templates".
  const disableDrawerPointerEvents = !!(
    mobileDrawerOpen ||
    mainMenuOverlayOpen ||
    welcomeMenuOpen ||
    roomModalOpen ||
    galleryOpen ||
    templatesGalleryOpen ||
    adminOpen
  )
  const fileInputRef = useRef(null)
  /** מצלמה בדפדפן: חייב להיות ב-DOM + קליק סינכרוני (אחרת iOS חוסם). */
  const cameraInputRef = useRef(null)
  const splitRefFileInputRef = useRef(null)
  const headerColorInputRef = useRef(null)
  const appRootRef = useRef(null)
  const bottomColorsRef = useRef(null)
  const canvasRef = useRef(null)
  const layersUnderlayRef = useRef(null)
  const overlayCanvasRef = useRef(null)
  const lastEventIdSeenRef = useRef(0)
  const pushSnapshotTimeoutRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const videoChunksRef = useRef([])
  const deviceIdRef = useRef(null)
  const nextToastIdRef = useRef(0)
  const lastAutosaveAtRef = useRef(0)
  const lastAutosaveSigRef = useRef('')
  const lastRoomSnapshotAtRef = useRef(0)
  const lastRoomSnapshotSigRef = useRef('')
  const roomPollDelayRef = useRef(1200)
  const [toasts, setToasts] = useState([])
  const [roomSyncIssue, setRoomSyncIssue] = useState('')
  const [layers, setLayers] = useState([{ id: 'layer-1', name: 'Layer 1', visible: true, dataUrl: null }])
  const [activeLayerId, setActiveLayerId] = useState('layer-1')
  const uploadPipelineRef = useRef('file')

  useEffect(() => {
    const appEl = appRootRef.current
    const colorsEl = bottomColorsRef.current
    if (!appEl || !colorsEl) return
    const updateBottomSpace = () => {
      const h = Math.max(92, Math.ceil(colorsEl.getBoundingClientRect().height || 0))
      appEl.style.setProperty('--bottom-colors-height', `${h}px`)
    }
    updateBottomSpace()
    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateBottomSpace)
      ro.observe(colorsEl)
    }
    window.addEventListener('resize', updateBottomSpace)
    window.addEventListener('orientationchange', updateBottomSpace)
    return () => {
      if (ro) ro.disconnect()
      window.removeEventListener('resize', updateBottomSpace)
      window.removeEventListener('orientationchange', updateBottomSpace)
    }
  }, [colors, effectiveExpertMode, childMode])

  useEffect(() => {
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem('colorphoto_outline_mode', outlineMode)
    } catch {
      // ignore
    }
  }, [outlineMode])

  useEffect(() => {
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem('colorphoto_outline_quality', outlineQuality)
    } catch {
      // ignore
    }
  }, [outlineQuality])

  useEffect(() => {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('colorphoto_line_assist_enabled', lineAssistEnabled ? '1' : '0')
        localStorage.setItem('colorphoto_line_assist_strength', String(lineAssistStrength))
      }
    } catch {
      // ignore
    }
  }, [lineAssistEnabled, lineAssistStrength])

  const outlineQualityOptions = useMemo(() => {
    // Presets tuned for: sharp lines, fewer holes, and predictable thickness.
    switch (outlineQuality) {
      case 'mandala':
        return {
          pipeline: 'canny',
          preprocessMandala: true,
          mandalaSegments: 12,
          mandalaMirror: true,
          useDeeplabMask: false,

          // Symmetric pattern: a bit of blur then crisp edges (noise rings less useful)
          blurPasses: 1,
          thinPasses: 0,

          hysteresisHighOffset: 5,
          hysteresisLowRatio: 0.46,

          closePasses: 2,
          erodePasses: 0,
          dilatePasses: 0,
          edgeSmoothPasses: 1,
          edgeSmoothThreshold: 4,
          openPasses: 0,

          pruneSpursIters: 0,
          objectMaskToEdges: false,

          postThinDilatePasses: 2,
          postThinMajorityPasses: 1,
          postThinMajorityThreshold: 3,

          strokeBandPasses: 0,
          finalClosePasses: 2,
          connectEndpoints: true,
          endpointMaxDist: 20,
          endpointMaxConnections: 280,
          endpointSkeletonIters: 2,
          postConnectThinIters: 1,
          minComponentSize: 4,
          finalSmoothPasses: 1,
          finalSmoothThreshold: 6,
        }
      case 'clean':
        return {
          pipeline: 'canny',
          // Less blur + no skeleton thin: keep fine features (eyes, lips, hair strands).
          blurPasses: 1,
          thinPasses: 0,
          useDeeplabMask: true,

          hysteresisHighOffset: 5,
          hysteresisLowRatio: 0.46,

          closePasses: 3,
          erodePasses: 0,
          dilatePasses: 1,
          edgeSmoothPasses: 1,
          edgeSmoothThreshold: 5,
          openPasses: 0,

          pruneSpursIters: 0,

          objectMaskToEdges: false,
          maskDilatePasses: 2,
          maskThresholdScale: 1.06,

          postThinDilatePasses: 2,
          postThinMajorityPasses: 1,
          postThinMajorityThreshold: 3,

          strokeBandPasses: 0,
          finalClosePasses: 2,
          connectEndpoints: true,
          endpointMaxDist: 22,
          endpointMaxConnections: 260,
          endpointSkeletonIters: 2,
          postConnectThinIters: 1,
          minComponentSize: 2,
          finalSmoothPasses: 1,
          finalSmoothThreshold: 5,
        }
      case 'sharp':
        return {
          pipeline: 'canny',
          blurPasses: 1,
          hysteresisHighOffset: 6,
          hysteresisLowRatio: 0.44,
          closePasses: 1,
          erodePasses: 0,
          dilatePasses: 2,
          edgeSmoothPasses: 1,
          edgeSmoothThreshold: 5,
          openPasses: 0,
          connectEndpoints: true,
          endpointMaxDist: 18,
          endpointMaxConnections: 220,
          endpointSkeletonIters: 2,
          postConnectThinIters: 1,
          minComponentSize: 4,
          finalSmoothPasses: 1,
          finalSmoothThreshold: 6,
        }
      case 'upscale':
        return {
          // HD photo -> coloring: upscale first, then use clean canny pass.
          enableUpscale: true,
          upscaleFactor: 1.75,
          pipeline: 'canny',
          blurPasses: 1,
          thinPasses: 0,
          useDeeplabMask: true,

          hysteresisHighOffset: 5,
          hysteresisLowRatio: 0.46,

          closePasses: 3,
          erodePasses: 0,
          dilatePasses: 1,
          edgeSmoothPasses: 1,
          edgeSmoothThreshold: 5,
          openPasses: 0,

          objectMaskToEdges: false,
          maskDilatePasses: 2,
          maskThresholdScale: 1.06,

          postThinDilatePasses: 2,
          postThinMajorityPasses: 1,
          postThinMajorityThreshold: 3,

          strokeBandPasses: 0,
          finalClosePasses: 2,
          connectEndpoints: true,
          endpointMaxDist: 24,
          endpointMaxConnections: 320,
          endpointSkeletonIters: 2,
          postConnectThinIters: 1,
          minComponentSize: 2,
          finalSmoothPasses: 1,
          finalSmoothThreshold: 5,
        }
      case 'soft':
        return {
          pipeline: 'shapeAware',
          blurPasses: 3,
          hysteresisHighOffset: 6,
          hysteresisLowRatio: 0.56,
          shapeOffset: 12,
          closePasses: 3,
          erodePasses: 0,
          dilatePasses: 2,
          edgeSmoothPasses: 2,
          edgeSmoothThreshold: 5,
          openPasses: 2,
          finalSmoothPasses: 1,
          finalSmoothThreshold: 6,
        }
      case 'standard':
        return {
          pipeline: 'shapeAware',
          blurPasses: 2,
          hysteresisHighOffset: 7,
          hysteresisLowRatio: 0.5,
          shapeOffset: 14,
          closePasses: 3,
          erodePasses: 1,
          dilatePasses: 2,
          edgeSmoothPasses: 1,
          edgeSmoothThreshold: 5,
          openPasses: 2,
          finalSmoothPasses: 1,
          finalSmoothThreshold: 6,
        }
      case 'perfect':
      default:
        return {
          pipeline: 'canny',
          blurPasses: 1,
          thinPasses: 0,
          useDeeplabMask: true,

          hysteresisHighOffset: 5,
          hysteresisLowRatio: 0.48,

          closePasses: 3,
          erodePasses: 0,
          dilatePasses: 1,
          edgeSmoothPasses: 1,
          edgeSmoothThreshold: 5,
          openPasses: 0,

          pruneSpursIters: 0,

          objectMaskToEdges: false,
          maskDilatePasses: 2,
          maskThresholdScale: 1.06,

          postThinDilatePasses: 2,
          postThinMajorityPasses: 1,
          postThinMajorityThreshold: 3,

          strokeBandPasses: 0,
          finalClosePasses: 2,
          connectEndpoints: true,
          endpointMaxDist: 22,
          endpointMaxConnections: 280,
          endpointSkeletonIters: 2,
          postConnectThinIters: 1,
          minComponentSize: 2,
          finalSmoothPasses: 1,
          finalSmoothThreshold: 5,
        }
    }
  }, [outlineQuality])

  const spawnStarTrail = useCallback((e) => {
    try {
      if (typeof window === 'undefined') return
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
      const touch = e?.touches?.[0] || e?.changedTouches?.[0]
      const clientX = touch?.clientX ?? e?.clientX
      const clientY = touch?.clientY ?? e?.clientY
      if (typeof clientX !== 'number' || typeof clientY !== 'number') return

      const root = document.createElement('div')
      root.className = 'kid-star-trail'
      root.style.left = `${clientX}px`
      root.style.top = `${clientY}px`
      document.body.appendChild(root)

      const colors = ['#ffd166', '#ff6b6b', '#4cc9f0', '#a8ffb5', '#c77dff']
      const count = 10
      for (let i = 0; i < count; i++) {
        const s = document.createElement('span')
        s.className = 'kid-star'
        const dx = (Math.random() - 0.5) * 120
        const dy = (Math.random() - 0.7) * 120
        const rot = (Math.random() - 0.5) * 220
        const delay = Math.random() * 80
        const size = 10 + Math.random() * 12
        const hue = colors[Math.floor(Math.random() * colors.length)]
        s.style.setProperty('--dx', `${dx}px`)
        s.style.setProperty('--dy', `${dy}px`)
        s.style.setProperty('--rot', `${rot}deg`)
        s.style.setProperty('--delay', `${delay}ms`)
        s.style.setProperty('--size', `${size}px`)
        s.style.setProperty('--color', hue)
        root.appendChild(s)
      }

      window.setTimeout(() => {
        root.remove()
      }, 850)
    } catch {
      // ignore particle errors
    }
  }, [])

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const showToast = useCallback((message, variant = 'info') => {
    const id = ++nextToastIdRef.current
    setToasts((prev) => [...prev, { id, type: 'message', message, variant }])
    return id
  }, [])

  const showPromptToast = useCallback((message, inputDefaultValue = '', onSubmit, onCancel, confirmLabel = 'אישור', cancelLabel = 'ביטול') => {
    const id = ++nextToastIdRef.current
    setToasts((prev) => [...prev, {
      id, type: 'prompt', message, variant: 'info', duration: 0,
      inputDefaultValue, confirmLabel, cancelLabel, onSubmit, onCancel,
    }])
    return id
  }, [])

  const showConfirmToast = useCallback((message, onConfirm, onCancel, confirmLabel = 'כן', cancelLabel = 'לא') => {
    const id = ++nextToastIdRef.current
    setToasts((prev) => [...prev, {
      id, type: 'confirm', message, variant: 'info', duration: 0,
      confirmLabel, cancelLabel, onConfirm, onCancel,
    }])
    return id
  }, [])

  const loadNewImage = useCallback(() => {
    setOverlayImage(null)
    setOutlineSize({ w: 0, h: 0 })
    setIsBlankPage(false)
    setEraserBaseImageUrl(null)
    outlineDataRef.current = null
    setUndoStack([]); setRedoStack([])
    setLayers([{ id: 'layer-1', name: 'Layer 1', visible: true, dataUrl: null }])
    setActiveLayerId('layer-1')
  }, [])

  const goToHome = useCallback(() => {
    setMainMenuOverlayOpen(true)
    setRoomId(null)
    setRoomInviteLink('')
    setRoomLiveEvents([])
    setRoomModalOpen(false)
  }, [])

  const startBlankPage = useCallback((size) => {
    const { w, h } = size || blankPageSize
    const safeW = Math.max(100, Math.min(4096, Math.round(w)))
    const safeH = Math.max(100, Math.min(4096, Math.round(h)))
    setOverlayImage(null)
    outlineDataRef.current = null
    setIsBlankPage(true)
    setEraserBaseImageUrl(null)
    setOutlineSize({ w: safeW, h: safeH })
    setUndoStack([]); setRedoStack([])
    setLayers([{ id: 'layer-1', name: 'Layer 1', visible: true, dataUrl: null }])
    setActiveLayerId('layer-1')
  }, [blankPageSize])

  const photoToPaintingCanvas = useCallback((sourceCanvas, options = {}) => {
    if (!sourceCanvas?.width || !sourceCanvas?.height) return sourceCanvas
    const w = sourceCanvas.width
    const h = sourceCanvas.height
    const output = document.createElement('canvas')
    output.width = w
    output.height = h
    const outCtx = output.getContext('2d')
    if (!outCtx) return sourceCanvas

    // First pass: mild blur/soften to reduce noisy photo texture.
    const tmp = document.createElement('canvas')
    tmp.width = w
    tmp.height = h
    const tctx = tmp.getContext('2d')
    if (tctx) {
      const scale = Math.max(0.45, Math.min(0.85, Number(options.scale ?? 0.62)))
      const sw = Math.max(1, Math.round(w * scale))
      const sh = Math.max(1, Math.round(h * scale))
      tctx.imageSmoothingEnabled = true
      tctx.imageSmoothingQuality = 'high'
      tctx.drawImage(sourceCanvas, 0, 0, sw, sh)
      outCtx.imageSmoothingEnabled = true
      outCtx.imageSmoothingQuality = 'high'
      outCtx.drawImage(tmp, 0, 0, sw, sh, 0, 0, w, h)
    } else {
      outCtx.drawImage(sourceCanvas, 0, 0)
    }

    // Second pass: posterize + contrast/saturation tuning for "painted" look.
    const imageData = outCtx.getImageData(0, 0, w, h)
    const data = imageData.data
    const levels = Math.max(4, Math.min(16, Math.round(options.levels ?? 7)))
    const step = 255 / (levels - 1)
    const contrast = Math.max(0.8, Math.min(1.5, Number(options.contrast ?? 1.12)))
    const saturation = Math.max(0.7, Math.min(1.6, Number(options.saturation ?? 1.18)))

    for (let i = 0; i < data.length; i += 4) {
      let r = data[i]
      let g = data[i + 1]
      let b = data[i + 2]

      // simple saturation boost
      const avg = (r + g + b) / 3
      r = avg + (r - avg) * saturation
      g = avg + (g - avg) * saturation
      b = avg + (b - avg) * saturation

      // contrast around midpoint
      r = ((r - 128) * contrast) + 128
      g = ((g - 128) * contrast) + 128
      b = ((b - 128) * contrast) + 128

      // quantize
      r = Math.round(Math.max(0, Math.min(255, r)) / step) * step
      g = Math.round(Math.max(0, Math.min(255, g)) / step) * step
      b = Math.round(Math.max(0, Math.min(255, b)) / step) * step

      data[i] = r
      data[i + 1] = g
      data[i + 2] = b
    }

    outCtx.putImageData(imageData, 0, 0)
    return output
  }, [])

  const applyImageToCanvas = useCallback(async (fileOrBlob, options = {}) => {
    const isUrl = typeof fileOrBlob === 'string'
    if (!fileOrBlob) return false
    if (!isUrl && !isLikelyImageFile(fileOrBlob)) return false
    /** רק מצלמה: המרה לקווי מתאר / היפוך וכו'. קובץ/URL/הדבקה — התמונה נשארת כפי שהיא (רק ירידה קנה מידה לזיכרון). */
    const imagePipeline = options?.imagePipeline === 'camera' ? 'camera' : 'file'
    try {
      const skipConfirm = options?.skipConfirm === true
      // Only ask if there is already work on the canvas (so we don't annoy users).
      // Converting/replacing an existing page would overwrite the current drawing.
      const hasWork = outlineSize.w > 0 && (undoStack.length > 0 || redoStack.length > 0)
      if (!skipConfirm && hasWork) {
        const shouldConvert = await new Promise((resolve) => {
          let settled = false
          const finish = (val) => {
            if (settled) return
            settled = true
            resolve(val)
          }
          const msg =
            imagePipeline === 'camera'
              ? (lang === 'en'
                ? 'You already have a drawing. Replace it by converting this image into outlines?'
                : 'כבר יש ציור בדף. להחליף ולהמיר את התמונה לקווי מתאר?')
              : (lang === 'en'
                ? 'You already have a drawing. Replace it with this image as-is (no outline conversion)?'
                : 'כבר יש ציור בדף. להחליף ולטעון את התמונה כפי שהיא (בלי המרה לקווי מתאר)?')
          showConfirmToast(
            msg,
            () => finish(true),
            () => finish(false),
            lang === 'en' ? 'Replace' : 'כן',
            lang === 'en' ? 'Cancel' : 'לא'
          )
        })

        if (!shouldConvert) return false
      }

      let maxLoad = typeof options.maxLoadSize === 'number'
        ? Math.min(4096, Math.max(256, Math.round(options.maxLoadSize)))
        : DEFAULT_MAX_IMAGE_LOAD
      // Native WebView: large canvases + outline pipeline often OOM; Deeplab is off anyway.
      if (isCapacitorNativeProd()) {
        maxLoad = Math.min(maxLoad, 1680)
      }
      const canvas = await loadImageToCanvas(fileOrBlob, maxLoad)
      setRestoreSnapshotUrl(null)

      if (imagePipeline === 'file') {
        outlineDataRef.current = null
        setOverlayImage(null)
        const shouldPaintify = options?.paintify === true
        if (options?.whitenTemplatePaper === true) {
          try {
            whitenColoringTemplatePaperInPlace(canvas)
          } catch (e) {
            console.warn('whitenColoringTemplatePaperInPlace skipped', e)
          }
        }
        const fileCanvas = shouldPaintify ? photoToPaintingCanvas(canvas) : canvas
        let snapshotUrl
        try {
          snapshotUrl = fileCanvas.toDataURL('image/png')
        } catch {
          snapshotUrl = fileCanvas.toDataURL('image/jpeg', 0.92)
        }
        setOutlineSize({ w: fileCanvas.width, h: fileCanvas.height })
        setIsBlankPage(false)
        setEraserBaseImageUrl(snapshotUrl)
        setUndoStack([]); setRedoStack([])
        setRestoreSnapshotUrl(snapshotUrl)
        setLayers([{ id: 'layer-1', name: 'Layer 1', visible: true, dataUrl: snapshotUrl }])
        setActiveLayerId('layer-1')
        if (shouldPaintify) {
          showToast(lang === 'en' ? 'Photo converted to painting.' : 'התמונה הומרה לסגנון ציור.', 'success')
        }
        return true
      }

      let outlineCanvas = null
      {
        const nativeCap = isCapacitorNativeProd()
        let outlineOpts = outlineQualityOptions || {}
        // שלב 1: שלילת תצוגה (בהיר↔כהה). שלב 2: מנדלה / Deeplab / קווי מתאר.
        let workCanvas = canvas
        if (!options?.skipInvert) {
          try {
            // צילום ממצלמה: invert לפני קווי מתאר סיבך את זיהוי הקצוות ולעיתים מפיק "קווים לבנים"; תמונה רגילה עובדת טוב יותר בלי שלב זה.
            if (imagePipeline !== 'camera') {
              workCanvas = invertRgbCanvas(canvas)
            }
          } catch (invErr) {
            console.warn('invertRgbCanvas skipped', invErr)
          }
        }

        if (outlineOpts.preprocessMandala === true) {
          try {
            workCanvas = photoToMandalaCanvas(workCanvas, {
              segments: outlineOpts.mandalaSegments ?? 12,
              mirror: outlineOpts.mandalaMirror !== false,
            })
            outlineOpts = { ...outlineOpts, useDeeplabMask: false }
          } catch (mErr) {
            console.warn('photoToMandalaCanvas skipped', mErr)
          }
        }

        if (outlineOpts.enableUpscale === true) {
          try {
            const maxEdge = nativeCap ? 2200 : 3600
            const longest = Math.max(workCanvas.width, workCanvas.height)
            const requestedFactor = Math.max(1, Number(outlineOpts.upscaleFactor || 1.6))
            const safeFactor = Math.min(requestedFactor, maxEdge / Math.max(1, longest))
            if (Number.isFinite(safeFactor) && safeFactor > 1.02) {
              const up = document.createElement('canvas')
              up.width = Math.max(1, Math.round(workCanvas.width * safeFactor))
              up.height = Math.max(1, Math.round(workCanvas.height * safeFactor))
              const uctx = up.getContext('2d')
              if (uctx) {
                uctx.imageSmoothingEnabled = true
                uctx.imageSmoothingQuality = 'high'
                uctx.drawImage(workCanvas, 0, 0, up.width, up.height)
                workCanvas = up
              }
            }
          } catch (upErr) {
            console.warn('upscale skipped', upErr)
          }
        }

        const {
          preprocessMandala: _pm,
          mandalaSegments: _ms,
          mandalaMirror: _mm,
          enableUpscale: _eu,
          upscaleFactor: _uf,
          ...outlineOptsForPipeline
        } = outlineOpts

        let pipelineOpts = { ...outlineOptsForPipeline }
        // App shell: no TF model/chunk fetch (avoids Failed to fetch) + less RAM.
        if (nativeCap) {
          pipelineOpts = { ...pipelineOpts, useDeeplabMask: false }
          delete pipelineOpts.objectMaskBinary
          delete pipelineOpts.objectMaskToEdges
        }
        if (pipelineOpts?.useDeeplabMask === true) {
          try {
            const mlMask = await getDeeplabObjectMaskBinary(workCanvas)
            pipelineOpts.objectMaskToEdges = true
            pipelineOpts.objectMaskBinary = mlMask
          } catch (dlErr) {
            console.warn('Deeplab mask skipped (network or WebView)', dlErr)
            pipelineOpts = { ...pipelineOpts, useDeeplabMask: false }
            delete pipelineOpts.objectMaskBinary
            delete pipelineOpts.objectMaskToEdges
          }
        }
        const mergedPipeline = { ...pipelineOpts, ...(options.outlinePipelineOpts || {}) }
        if (mergedPipeline.useDeeplabMask === false) {
          delete mergedPipeline.objectMaskBinary
          delete mergedPipeline.objectMaskToEdges
        }
        outlineCanvas = imageToOutlines(workCanvas, mergedPipeline)
        try {
          normalizeOutlineCanvasPolarity(outlineCanvas)
        } catch (polErr) {
          console.warn('normalizeOutlineCanvasPolarity skipped', polErr)
        }
      }
      // Barrier for fill tool: tolerate small gaps by dilating/closing the outline mask.
      outlineDataRef.current = outlineToFillBarrier(outlineCanvas, { lineLumThreshold: 110, dilatePasses: 4, closePasses: 3 })
      const overlayCanvas = outlineToOverlay(outlineCanvas)
      let overlayDataUrl
      try {
        overlayDataUrl = overlayCanvas.toDataURL('image/png')
      } catch {
        overlayDataUrl = overlayCanvas.toDataURL('image/jpeg', 0.92)
      }
      setOverlayImage(overlayDataUrl)
      setOutlineSize({ w: outlineCanvas.width, h: outlineCanvas.height })
      setIsBlankPage(false)
      setEraserBaseImageUrl(null)
      setUndoStack([]); setRedoStack([])
      setLayers([{ id: 'layer-1', name: 'Layer 1', visible: true, dataUrl: null }])
      setActiveLayerId('layer-1')
      // UX safety: after camera capture, force a visible paint setup.
      // This avoids common confusion where user remains with eraser / light paint style / near-white color.
      if (imagePipeline === 'camera') {
        if (tool !== 'brush' && tool !== 'pencil' && tool !== 'marker') setTool('brush')
        if (paintStyle !== 'normal') setPaintStyle('normal')
        if (isVeryLightHex(currentColor)) {
          setCurrentColor('#ff6b6b')
        }
      }
      return true
    } catch (err) {
      console.error(err)
      const msg = err?.message ? `: ${err.message}` : ''
      showToast(`שגיאה בטעינת התמונה${msg}`, 'error')
      return false
    }
  }, [showToast, showConfirmToast, lang, outlineSize.w, undoStack.length, redoStack.length, outlineQualityOptions, photoToPaintingCanvas, tool, setTool, currentColor, paintStyle])

  const handleFileSelect = useCallback(async (e) => {
    const file = e?.target?.files?.[0]
    if (!file) return
    const mode = uploadPipelineRef.current === 'painting' ? 'painting' : 'file'
    uploadPipelineRef.current = 'file'
    if (mode === 'painting') {
      await applyImageToCanvas(file, { imagePipeline: 'file', paintify: true })
    } else {
      await applyImageToCanvas(file, { imagePipeline: 'file' })
    }
    if (e?.target) e.target.value = ''
  }, [applyImageToCanvas])

  const handleCameraFileSelect = useCallback(async (e) => {
    const file = e?.target?.files?.[0]
    if (!file) return
    await applyImageToCanvas(file, { imagePipeline: 'camera' })
    if (e?.target) e.target.value = ''
  }, [applyImageToCanvas])

  /** גלריה/קובץ: רק input file — בלי Camera plugin (שמקודד מחדש ומשנה תמונות מגלריה). */
  const triggerMainImageUpload = useCallback(() => {
    uploadPipelineRef.current = 'file'
    fileInputRef.current?.click()
  }, [])

  const triggerPhotoToPaintingUpload = useCallback(() => {
    uploadPipelineRef.current = 'painting'
    fileInputRef.current?.click()
  }, [])

  const triggerSplitRefUpload = useCallback(() => {
    splitRefFileInputRef.current?.click()
  }, [])

  const openCamera = useCallback(async () => {
    // ב-Web/PWA אסור לעשות await לפני click — אחרת נאבד user activation ו-iOS/Android יחסמו את בורר הקבצים/מצלמה.
    if (!Capacitor.isNativePlatform()) {
      cameraInputRef.current?.click()
      return
    }
    const r = await pickFromCameraOnly()
    if (r.status === 'ok') {
      await applyImageToCanvas(r.dataUrl, { imagePipeline: 'camera' })
      return
    }
    if (r.status === 'fallback') {
      cameraInputRef.current?.click()
    }
  }, [applyImageToCanvas])

  const getImageFromClipboard = useCallback(async () => {
    if (navigator.clipboard?.read) {
      const items = await navigator.clipboard.read()
      for (const item of items) {
        const imageType = item.types.find((t) => t.startsWith('image/'))
        if (imageType) {
          const blob = await item.getType(imageType)
          if (blob) return await new Promise((resolve) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.readAsDataURL(blob) })
        }
      }
    }
    return null
  }, [])

  const pasteImageAsReference = useCallback(async () => {
    try {
      const dataUrl = await getImageFromClipboard()
      if (dataUrl) {
        setSplitRefImageUrl(dataUrl)
        setMobileDrawerOpen(false)
      } else showToast('אין תמונה בלוח. העתיקו תמונה (למשל צילום מסך) ואז לחצו שוב.', 'error')
    } catch (e) {
      if (e?.name !== 'NotAllowedError') showToast('לא ניתן לגשת ללוח. נסו להדביק עם Ctrl+V או Cmd+V.', 'error')
    }
  }, [getImageFromClipboard, showToast])

  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

  const pasteImageFromClipboard = useCallback(async () => {
    try {
      if (splitMode) {
        const dataUrl = await getImageFromClipboard()
        if (dataUrl) { setSplitRefImageUrl(dataUrl); setMobileDrawerOpen(false) }
        else showToast('אין תמונה בלוח.', 'error')
        return
      }
      if (navigator.clipboard?.read) {
        const items = await navigator.clipboard.read()
        for (const item of items) {
          const imageType = item.types.find((t) => t.startsWith('image/'))
          if (imageType) {
            const blob = await item.getType(imageType)
            if (blob) {
              const ok = await applyImageToCanvas(blob, { imagePipeline: 'file' })
              if (ok) setMobileDrawerOpen(false)
              return
            }
          }
        }
      }
      if (isMobileDevice) {
        showToast('במכשיר נייד הלוח לא זמין. השתמשו ב"העלאה מקובץ" או "צילום" להעלאת תמונה.', 'error')
        void triggerMainImageUpload()
      } else {
        showToast('אין תמונה בלוח. העתיקו תמונה (למשל צילום מסך) והדביקו עם Ctrl+V או Cmd+V.', 'error')
      }
    } catch (e) {
      if (e?.name === 'NotAllowedError' && isMobileDevice) {
        showToast('במכשיר נייד: השתמשו ב"העלאה מקובץ" או "צילום" להעלאת תמונה.', 'error')
        void triggerMainImageUpload()
      } else if (e?.name !== 'NotAllowedError') {
        showToast('לא ניתן לגשת ללוח. במחשב: Ctrl+V או Cmd+V. בנייד: העלאה מקובץ.', 'error')
      }
    }
  }, [applyImageToCanvas, splitMode, getImageFromClipboard, showToast, triggerMainImageUpload])

  const toggleExpertMode = useCallback(() => {
    setExpertMode((e) => {
      if (!e) return true
      setTool('brush')
      return false
    })
  }, [setTool])
  const toggleSplitMode = useCallback(() => {
    if (!splitMode) {
      if (outlineSize.w === 0) startBlankPage()
      setSplitMode(true)
    } else {
      setSplitMode(false)
      setSplitRefImageUrl(null)
    }
  }, [outlineSize.w, splitMode, startBlankPage])

  useEffect(() => {
    const onPaste = async (e) => {
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile()
          if (file) {
            e.preventDefault()
            if (splitMode) {
              const dataUrl = await new Promise((resolve) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.readAsDataURL(file) })
              if (dataUrl) setSplitRefImageUrl(dataUrl)
            } else applyImageToCanvas(file, { imagePipeline: 'file' })
            return
          }
        }
      }
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [applyImageToCanvas, splitMode])

  const openDefaultColoringPage = useCallback((char) => {
    try {
      const canvas = createCharacterCanvas(char)
      const outlineCanvas = imageToOutlines(canvas, IMAGE_TO_OUTLINES_LINE_ART)
      outlineDataRef.current = outlineToFillBarrier(outlineCanvas, { lineLumThreshold: 110, dilatePasses: 4, closePasses: 3 })
      const overlayCanvas = outlineToOverlay(outlineCanvas)
      setOverlayImage(overlayCanvas.toDataURL('image/png'))
      setOutlineSize({ w: outlineCanvas.width, h: outlineCanvas.height })
      setIsBlankPage(false)
      setEraserBaseImageUrl(null)
      setUndoStack([]); setRedoStack([])
    } catch (err) {
      console.error(err)
      showToast('שגיאה בפתיחת דף הצביעה', 'error')
    }
  }, [showToast])

  /** דף כל האותיות — latin = A–Z, hebrew = אלף-בית */
  const openAlphabetGridPage = useCallback((script) => {
    try {
      const canvas = script === 'hebrew' ? createHebrewAlphabetCanvas() : createAbcCanvas()
      const outlineCanvas = imageToOutlines(canvas, IMAGE_TO_OUTLINES_LINE_ART)
      outlineDataRef.current = outlineToFillBarrier(outlineCanvas, { lineLumThreshold: 110, dilatePasses: 4, closePasses: 3 })
      const overlayCanvas = outlineToOverlay(outlineCanvas)
      setOverlayImage(overlayCanvas.toDataURL('image/png'))
      setOutlineSize({ w: outlineCanvas.width, h: outlineCanvas.height })
      setIsBlankPage(false)
      setEraserBaseImageUrl(null)
      setUndoStack([]); setRedoStack([])
    } catch (err) {
      console.error(err)
      showToast(script === 'hebrew' ? 'שגיאה ביצירת דף אלף-בית' : 'שגיאה ביצירת דף ABC', 'error')
    }
  }, [showToast])

  const openWordPage = useCallback((word) => {
    const w = ((word ?? composeWord) || '').trim()
    if (!w) {
      showToast('כתבי מילה בשדה', 'error')
      return false
    }
    try {
      const canvas = createWordCanvas(w)
      const outlineCanvas = imageToOutlines(canvas, IMAGE_TO_OUTLINES_LINE_ART)
      outlineDataRef.current = outlineToFillBarrier(outlineCanvas, { lineLumThreshold: 110, dilatePasses: 4, closePasses: 3 })
      const overlayCanvas = outlineToOverlay(outlineCanvas)
      setOverlayImage(overlayCanvas.toDataURL('image/png'))
      setOutlineSize({ w: outlineCanvas.width, h: outlineCanvas.height })
      setIsBlankPage(false)
      setEraserBaseImageUrl(null)
      setUndoStack([]); setRedoStack([])
      setWelcomeMenuOpen(false)
      return true
    } catch (err) {
      console.error(err)
      showToast(err?.message || 'שגיאה ביצירת דף המילה', 'error')
      return false
    }
  }, [composeWord, showToast])

  const applyOutlineCanvas = useCallback((outlineCanvas) => {
    try {
      if (!outlineCanvas) return
      outlineDataRef.current = outlineToFillBarrier(outlineCanvas, { lineLumThreshold: 110, dilatePasses: 4, closePasses: 3 })
      const overlayCanvas = outlineToOverlay(outlineCanvas)
      setOverlayImage(overlayCanvas.toDataURL('image/png'))
      setOutlineSize({ w: outlineCanvas.width, h: outlineCanvas.height })
      setIsBlankPage(false)
      setEraserBaseImageUrl(null)
      setUndoStack([])
      setRedoStack([])
    } catch (err) {
      console.error(err)
      showToast('שגיאה בטעינת הצורה', 'error')
    }
  }, [showToast])

  const [imageSearchQuery, setImageSearchQuery] = useState('דפי צביעה חינם לילדים')
  const [imageSearchLoading, setImageSearchLoading] = useState(false)
  const [imageSearchError, setImageSearchError] = useState('')
  const [imageSearchResults, setImageSearchResults] = useState([])
  const [imageSearchModalOpen, setImageSearchModalOpen] = useState(false)

  const importImageFromUrl = useCallback(async (url, filename = 'image.jpg', options = {}) => {
    try {
      setImageSearchError('')
      const ok = await applyImageToCanvas(url, options)
      return !!ok
    } catch (err) {
      console.error(err)
      showToast('שגיאה בטעינת התמונה מהחיפוש', 'error')
      return false
    }
  }, [applyImageToCanvas, showToast])

  /** תבניות תמונה: אותה תמונה כמו במקור — רק רקע נייר מולבן (בלי המרה לקווי מתאר). */
  const openTemplateImageAsColoringPage = useCallback(
    async (url) => {
      const ok = await importImageFromUrl(url, 'template.png', {
        skipConfirm: true,
        imagePipeline: 'file',
        whitenTemplatePaper: true,
        maxLoadSize: TEMPLATE_MAX_IMAGE_LOAD,
      })
      if (!ok) return
      if (childMode) setEffectTrigger({ key: Date.now(), type: 'sparkle' })
      setTemplatesGalleryOpen(false)
    },
    [importImageFromUrl, childMode]
  )

  const searchImages = useCallback(async (query) => {
    if (offlineStrictMode) {
      setImageSearchError('במצב אופליין מלא חיפוש אינטרנטי כבוי. העלו קובץ מקומי.')
      setImageSearchResults([])
      return
    }
    const q = (query ?? imageSearchQuery).trim()
    if (!q) return
    setImageSearchLoading(true)
    setImageSearchError('')
    setImageSearchResults([])
    try {
      const apiUrl = new URL('https://commons.wikimedia.org/w/api.php')
      apiUrl.searchParams.set('action', 'query')
      apiUrl.searchParams.set('format', 'json')
      apiUrl.searchParams.set('origin', '*')
      apiUrl.searchParams.set('generator', 'search')
      apiUrl.searchParams.set('gsrsearch', q)
      apiUrl.searchParams.set('gsrlimit', '18')
      apiUrl.searchParams.set('gsrnamespace', '6')
      apiUrl.searchParams.set('prop', 'imageinfo')
      apiUrl.searchParams.set('iiprop', 'url')
      apiUrl.searchParams.set('iiurlwidth', '260')

      const res = await fetch(apiUrl.toString())
      if (!res.ok) throw new Error(`search failed ${res.status}`)
      const data = await res.json()
      const pages = Object.values(data?.query?.pages || {})
      const results = pages
        .map((p) => {
          const info = p?.imageinfo?.[0]
          if (!info?.url || !info?.thumburl) return null
          return {
            title: p?.title?.replace(/^File:/, '') || 'image',
            thumbUrl: info.thumburl,
            fullUrl: info.url,
          }
        })
        .filter(Boolean)
      setImageSearchResults(results)
      setImageSearchModalOpen(true)
      if (results.length === 0) setImageSearchError('לא נמצאו תוצאות. נסו ניסוח אחר.')
    } catch (err) {
      console.error(err)
      setImageSearchError('שגיאה בחיפוש. נסו שוב.')
    } finally {
      setImageSearchLoading(false)
    }
  }, [imageSearchQuery, offlineStrictMode])

  const openGoogleImageSearch = useCallback((query) => {
    if (offlineStrictMode) {
      showToast('במצב אופליין מלא גוגל תמונות לא זמין. השתמשו בהעלאה מקומית.', 'info')
      return
    }
    const q = encodeURIComponent((query ?? imageSearchQuery ?? 'דפי צביעה חינם לילדים').trim())
    window.open(`https://www.google.com/search?tbm=isch&q=${q}`, '_blank', 'noopener,noreferrer')
  }, [imageSearchQuery, showToast, offlineStrictMode])

  const applyTemplate = useCallback((shapeId) => {
    try {
      if (shapeId?.startsWith('char-')) {
        const name = shapeId.slice(5)
        const q = `${name} דף צביעה שחור לבן`
        openGoogleImageSearch(q)
        return
      }
      if (shapeId?.startsWith('geo-')) {
        const outlineCanvas = createGeometricOutlineCanvas(shapeId.slice(4), { size: 640, padding: 0.14, lineWidth: 8 })
        applyOutlineCanvas(outlineCanvas)
        if (childMode) setEffectTrigger({ key: Date.now(), type: 'sparkle' })
        return
      }
      const canvas = shapeId?.startsWith('he-') ? createCharacterCanvas(shapeId.slice(3)) : createTemplateShapeCanvas(shapeId)
      const outlineCanvas = imageToOutlines(canvas, IMAGE_TO_OUTLINES_LINE_ART)
      applyOutlineCanvas(outlineCanvas)
      if (childMode) setEffectTrigger({ key: Date.now(), type: 'sparkle' })
    } catch (err) {
      console.error(err)
      showToast('שגיאה בטעינת התבנית', 'error')
    }
  }, [applyOutlineCanvas, showToast, openGoogleImageSearch, childMode])

  const pushUndo = useCallback((dataUrl) => {
    setRedoStack([])
    setUndoStack((s) => (s.length >= 30 ? [...s.slice(1), dataUrl] : [...s, dataUrl]))
  }, [])

  const undo = useCallback(() => {
    if (undoStack.length === 0 || !canvasRef.current) return
    const currentUrl = canvasRef.current.toDataURL('image/png')
    const last = undoStack[undoStack.length - 1]
    setRedoStack((s) => (s.length >= 30 ? [...s.slice(1), currentUrl] : [...s, currentUrl]))
    setUndoStack((s) => s.slice(0, -1))
    const img = new Image()
    img.onload = () => {
      const ctx = canvasRef.current.getContext('2d')
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
      ctx.drawImage(img, 0, 0)
      if (activeLayerId) {
        setLayers((prev) => prev.map((layer) => (
          layer.id === activeLayerId ? { ...layer, dataUrl: last } : layer
        )))
      }
    }
    img.src = last
  }, [undoStack, activeLayerId])

  const redo = useCallback(() => {
    if (redoStack.length === 0 || !canvasRef.current) return
    const currentUrl = canvasRef.current.toDataURL('image/png')
    const next = redoStack[redoStack.length - 1]
    setUndoStack((s) => (s.length >= 30 ? [...s.slice(1), currentUrl] : [...s, currentUrl]))
    setRedoStack((s) => s.slice(0, -1))
    const img = new Image()
    img.onload = () => {
      const ctx = canvasRef.current.getContext('2d')
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
      ctx.drawImage(img, 0, 0)
      if (activeLayerId) {
        setLayers((prev) => prev.map((layer) => (
          layer.id === activeLayerId ? { ...layer, dataUrl: next } : layer
        )))
      }
    }
    img.src = next
  }, [redoStack, activeLayerId])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (mobileDrawerOpen) {
          e.preventDefault()
          setMobileDrawerOpen(false)
          return
        }
        const tag = e.target?.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return
        if (typeof window !== 'undefined' && window.matchMedia('(max-width: 899px)').matches && toolsSideOpen) {
          e.preventDefault()
          setToolsSideOpen(false)
        }
        return
      }
      const tag = e.target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return
      const mod = e.metaKey || e.ctrlKey
      if (!mod) return
      if (e.key === 'z' || e.key === 'Z') {
        if (e.shiftKey) {
          e.preventDefault()
          redo()
        } else {
          e.preventDefault()
          undo()
        }
      } else if (e.key === 'y' && !e.shiftKey) {
        e.preventDefault()
        redo()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [undo, redo, mobileDrawerOpen, toolsSideOpen])

  const clearColors = useCallback(() => {
    if (!canvasRef.current) return
    if (!overlayImage && !isBlankPage) return
    pushUndo(canvasRef.current.toDataURL('image/png'))
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    if (activeLayerId === 'layer-1') {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }
    try {
      const snapshot = canvas.toDataURL('image/png')
      setLayers((prev) => prev.map((layer) => (
        layer.id === activeLayerId ? { ...layer, dataUrl: snapshot } : layer
      )))
    } catch {
      // ignore snapshot errors
    }
  }, [overlayImage, isBlankPage, pushUndo, activeLayerId])

  const openGallery = useCallback(() => {
    setGalleryItems(getGallery())
    setGalleryOpen(true)
    setMobileDrawerOpen(false)
  }, [])

  const createRoomAndInvite = useCallback(async () => {
    if (offlineStrictMode) {
      showToast('מצב אופליין: חדרים משותפים כבויים. כל הכלים עובדים מקומית.', 'info')
      return
    }
    setRoomCreating(true)
    try {
      const id = await createRoom(deviceIdRef.current)
      const link = `${getPublicAppBaseUrl()}?room=${id}`
      window.history.pushState({}, '', link)
      setRoomId(id)
      setRoomInviteLink(link)
      startBlankPage()
      setRoomModalOpen(true)
    } catch {
      const appLink = getPublicAppBaseUrl()
      showConfirmToast(
        'שיתוף חדר ציור דורש שהאפליקציה תהיה על שרת עם PHP (תיקיית api). בינתיים: להעתיק את קישור האפליקציה ולשלוח לחברים?',
        () => {
          if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(appLink).then(() => showToast('קישור האפליקציה הועתק ללוח - שלחו לחברים.', 'success')).catch(() => {})
          }
        },
        undefined,
        'העתק קישור',
        'ביטול'
      )
    } finally {
      setRoomCreating(false)
    }
  }, [startBlankPage, showToast, showConfirmToast, offlineStrictMode])

  const copyRoomLink = useCallback(() => {
    if (!roomInviteLink) return
    navigator.clipboard?.writeText(roomInviteLink).then(() => showToast('הקישור הועתק ללוח', 'success')).catch(() => {})
  }, [roomInviteLink, showToast])

  const shareRoomLink = useCallback(async () => {
    if (!roomInviteLink) return
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'חדר ציור משותף - דפי צביעה',
          text: 'הצטרפו אלי לחדר ציור',
          url: roomInviteLink,
        })
      } else {
        copyRoomLink()
      }
    } catch (e) {
      if (e?.name !== 'AbortError') copyRoomLink()
    }
  }, [roomInviteLink, copyRoomLink])

  const loadCompositeIntoCanvas = useCallback((dataUrl) => {
    const img = new Image()
    img.onload = () => {
      setOverlayImage(null)
      outlineDataRef.current = null
      setIsBlankPage(false)
      setEraserBaseImageUrl(dataUrl)
      setOutlineSize({ w: img.width, h: img.height })
      setUndoStack([]); setRedoStack([])
      setRestoreSnapshotUrl(dataUrl)
      setLayers([{ id: 'layer-1', name: 'Layer 1', visible: true, dataUrl }])
      setActiveLayerId('layer-1')
    }
    img.src = dataUrl
  }, [])

  const getCompositeCanvas = useCallback(() => {
    if (!canvasRef.current || outlineSize.w === 0) return null
    const w = outlineSize.w
    const h = outlineSize.h
    const composite = document.createElement('canvas')
    composite.width = w
    composite.height = h
    const ctx = composite.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, w, h)
    if (layersUnderlayRef.current) ctx.drawImage(layersUnderlayRef.current, 0, 0)
    const activeLayer = layers.find((layer) => layer.id === activeLayerId)
    if (!activeLayer || activeLayer.visible) ctx.drawImage(canvasRef.current, 0, 0)
    // מצב "lines" כולל קווי מתאר בשחור מעל הצביעה (כיום).
    // Keep template lines as a separate top layer so eraser never deletes them.
    if (!isBlankPage && overlayCanvasRef.current) ctx.drawImage(overlayCanvasRef.current, 0, 0)
    return composite
  }, [outlineSize.w, outlineSize.h, isBlankPage, outlineMode, layers, activeLayerId])

  const saveToGallery = useCallback(() => {
    const composite = getCompositeCanvas()
    if (!composite) {
      showToast('לא ניתן לשמור - אין ציור לשמור. ציירו משהו או טענו דף צביעה.', 'error')
      return
    }
    try {
      const dataUrl = composite.toDataURL('image/png')
      const userSlots = getGallery().filter((i) => i.id !== GALLERY_AUTOSAVE_ID)
      const name = String(userSlots.length + 1)
      addToGallery(dataUrl, name)
      setGalleryItems(getGallery())
      setMobileDrawerOpen(false)
      showToast('נשמר לגלריה', 'success')
      showConfirmToast('לשמור גם כקובץ במכשיר?', () => {
        const link = document.createElement('a')
        link.download = `${name}.png`
        link.href = dataUrl
        link.click()
      }, undefined, 'שמור קובץ', 'לא עכשיו')
    } catch (e) {
      const msg = e?.name === 'QuotaExceededError' || e?.message?.includes('QuotaExceeded')
        ? 'אין מקום בשמירה (הגלריה או האחסון מלאים). נסו למחוק פריטים מהגלריה.'
        : (e?.message || 'שגיאה בשמירה לגלריה')
      showToast(msg, 'error')
    }
  }, [getCompositeCanvas, showToast, showConfirmToast])

  const takeScreenshot = useCallback(() => {
    const composite = getCompositeCanvas()
    if (!composite) {
      showToast(lang === 'en' ? 'No drawing to capture yet.' : 'אין ציור לצילום מסך עדיין.', 'error')
      return
    }
    try {
      const dataUrl = composite.toDataURL('image/png')
      const link = document.createElement('a')
      link.download = `coloring-screenshot-${Date.now()}.png`
      link.href = dataUrl
      link.click()
      showToast(lang === 'en' ? 'Screenshot saved.' : 'צילום מסך נשמר.', 'success')
    } catch (e) {
      console.error(e)
      showToast(lang === 'en' ? 'Could not save screenshot.' : 'לא הצלחנו לשמור צילום מסך.', 'error')
    }
  }, [getCompositeCanvas, showToast, lang])

  const resizePicture = useCallback(() => {
    const composite = getCompositeCanvas()
    if (!composite) {
      showToast(lang === 'en' ? 'No picture to resize yet.' : 'אין תמונה לשינוי גודל עדיין.', 'error')
      return
    }
    const defaultValue = `${outlineSize.w || composite.width}x${outlineSize.h || composite.height}`
    showPromptToast(
      lang === 'en'
        ? 'Enter new size as WIDTHxHEIGHT (example: 1200x900)'
        : 'הזינו גודל חדש בפורמט רוחבxגובה (לדוגמה: 1200x900)',
      defaultValue,
      (value) => {
        const raw = String(value || '').trim().toLowerCase()
        const m = raw.match(/^(\d{2,5})\s*[x×]\s*(\d{2,5})$/)
        if (!m) {
          showToast(lang === 'en' ? 'Invalid size format. Use WIDTHxHEIGHT.' : 'פורמט לא תקין. השתמשו ב־רוחבxגובה.', 'error')
          return
        }
        const w = Math.max(100, Math.min(4096, Number(m[1])))
        const h = Math.max(100, Math.min(4096, Number(m[2])))
        if (!Number.isFinite(w) || !Number.isFinite(h)) {
          showToast(lang === 'en' ? 'Invalid size values.' : 'ערכי גודל לא תקינים.', 'error')
          return
        }
        try {
          const resized = document.createElement('canvas')
          resized.width = w
          resized.height = h
          const ctx = resized.getContext('2d')
          if (!ctx) {
            showToast(lang === 'en' ? 'Resize failed (canvas context).' : 'שינוי גודל נכשל (אין canvas context).', 'error')
            return
          }
          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'
          ctx.fillStyle = '#ffffff'
          ctx.fillRect(0, 0, w, h)
          ctx.drawImage(composite, 0, 0, composite.width, composite.height, 0, 0, w, h)
          const dataUrl = resized.toDataURL('image/png')
          loadCompositeIntoCanvas(dataUrl)
          showToast(lang === 'en' ? `Resized to ${w}×${h}.` : `התמונה שונתה ל־${w}×${h}.`, 'success')
        } catch (e) {
          console.error(e)
          showToast(lang === 'en' ? 'Could not resize picture.' : 'לא הצלחנו לשנות גודל לתמונה.', 'error')
        }
      },
      undefined,
      lang === 'en' ? 'Apply' : 'החל',
      lang === 'en' ? 'Cancel' : 'ביטול',
    )
  }, [getCompositeCanvas, showToast, showPromptToast, lang, outlineSize.w, outlineSize.h, loadCompositeIntoCanvas])

  const removeBackground = useCallback(async () => {
    const composite = getCompositeCanvas()
    if (!composite) {
      showToast(lang === 'en' ? 'No picture to process yet.' : 'אין תמונה לעיבוד עדיין.', 'error')
      return
    }
    try {
      showToast(lang === 'en' ? 'Removing background…' : 'מסיר רקע…', 'info')
      const w = composite.width
      const h = composite.height
      const mask = await getDeeplabObjectMaskBinary(composite)
      if (!mask || mask.length !== w * h) {
        showToast(lang === 'en' ? 'Background removal failed.' : 'הסרת הרקע נכשלה.', 'error')
        return
      }
      const out = document.createElement('canvas')
      out.width = w
      out.height = h
      const outCtx = out.getContext('2d', { willReadFrequently: true })
      const srcCtx = composite.getContext('2d', { willReadFrequently: true })
      if (!outCtx || !srcCtx) {
        showToast(lang === 'en' ? 'Background removal is unavailable.' : 'הסרת רקע לא זמינה כרגע.', 'error')
        return
      }
      const srcData = srcCtx.getImageData(0, 0, w, h)
      const data = srcData.data
      for (let i = 0; i < w * h; i++) {
        const p = i * 4
        if (mask[i] === 1) {
          data[p + 3] = 255
        } else {
          data[p] = 255
          data[p + 1] = 255
          data[p + 2] = 255
          data[p + 3] = 255
        }
      }
      outCtx.putImageData(srcData, 0, 0)
      const dataUrl = out.toDataURL('image/png')
      loadCompositeIntoCanvas(dataUrl)
      showToast(lang === 'en' ? 'Background removed.' : 'הרקע הוסר.', 'success')
    } catch (e) {
      console.error(e)
      showToast(lang === 'en' ? 'Could not remove background.' : 'לא הצלחנו להסיר רקע.', 'error')
    }
  }, [getCompositeCanvas, showToast, lang, loadCompositeIntoCanvas])

  useEffect(() => {
    const autosave = () => {
      try {
        if (outlineSize.w === 0 || outlineSize.h === 0) return
        const composite = getCompositeCanvas()
        if (!composite) return
        const now = Date.now()
        if (now - lastAutosaveAtRef.current < 20000) return
        const dataUrl = composite.toDataURL('image/png')
        const sig = `${dataUrl.length}-${outlineSize.w}x${outlineSize.h}-${isBlankPage ? 'blank' : 'img'}`
        if (sig === lastAutosaveSigRef.current) return
        upsertAutosave(dataUrl)
        lastAutosaveAtRef.current = now
        lastAutosaveSigRef.current = sig
      } catch {
        // ignore autosave errors (quota/private mode)
      }
    }

    // periodic autosave
    const t = window.setInterval(autosave, 30000)

    // quick autosave when tab is hidden / leaving
    const onVis = () => { if (document.visibilityState === 'hidden') autosave() }
    const onUnload = () => autosave()
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('beforeunload', onUnload)

    return () => {
      window.clearInterval(t)
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('beforeunload', onUnload)
    }
  }, [getCompositeCanvas, outlineSize.w, outlineSize.h, isBlankPage])

  const shareDrawing = useCallback(async () => {
    const composite = getCompositeCanvas()
    if (!composite) return
    const dataUrl = composite.toDataURL('image/png')
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'ציור מדפי הצביעה',
          text: 'שיתפתי ציור מהאפליקציה דפי צביעה.',
          url: dataUrl,
        })
      } else {
        const link = document.createElement('a')
        link.download = 'coloring-page.png'
        link.href = dataUrl
        link.click()
      }
    } catch {
      // משתמש ביטל או שגיאה – מתעלמים בשקט
    }
  }, [getCompositeCanvas])

  const shareApp = useCallback(async () => {
    if (offlineStrictMode) {
      showToast('מצב אופליין: שיתוף קישור חיצוני כבוי.', 'info')
      return
    }
    const url = getPublicAppBaseUrl()
    const text = 'דפי צביעה מהתמונות שלך – נסה את COLORDEV.ART'
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'דפי צביעה',
          text,
          url,
        })
      } else {
        await navigator.clipboard?.writeText?.(url)
        showToast('קישור לאפליקציה הועתק ללוח', 'success')
      }
    } catch {
      // משתמש ביטל – לא מציגים שגיאה
    }
  }, [showToast, offlineStrictMode])

  const openExportModal = useCallback(() => {
    if (!getCompositeCanvas()) {
      showToast('אין ציור לייצא. ציירו משהו קודם.', 'error')
      return
    }
    setExportModalOpen(true)
  }, [getCompositeCanvas, showToast])

  const requestFlowText = useCallback(() => {
    return window.prompt(
      lang === 'en' ? 'Flow chart label (one line):' : 'טקסט בתרשים (שורה אחת):',
      '',
    )
  }, [lang])

  const applyFlowEditorCanvas = useCallback((canvas) => {
    try {
      if (!canvas) return
      const dataUrl = canvas.toDataURL('image/png')
      loadCompositeIntoCanvas(dataUrl)
      showToast(lang === 'en' ? 'Flow diagram added to the page.' : 'התרשים נוסף לדף.', 'success')
    } catch (e) {
      console.error(e)
      showToast(lang === 'en' ? 'Could not apply flow diagram.' : 'לא הצלחנו להוסיף את התרשים.', 'error')
    }
  }, [loadCompositeIntoCanvas, showToast, lang])

  const applyFilterToPicture = useCallback((dataUrl) => {
    if (!dataUrl) return
    loadCompositeIntoCanvas(dataUrl)
    showToast(lang === 'en' ? 'Filter applied to picture.' : 'הפילטר הוחל על התמונה.', 'success')
  }, [loadCompositeIntoCanvas, showToast, lang])

  const startVideoRecording = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || outlineSize.w === 0) {
      showToast('פתחו דף ציור לפני תחילת ההקלטה.', 'error')
      return
    }
    try {
      const stream = canvas.captureStream(10)
      const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm'
      const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 2500000 })
      videoChunksRef.current = []
      recorder.ondataavailable = (e) => { if (e.data.size) videoChunksRef.current.push(e.data) }
      recorder.onstop = () => {
        if (!videoChunksRef.current.length) {
          showToast(lang === 'en' ? 'Recording stopped (no frames captured).' : 'ההקלטה נעצרה (לא נקלט וידאו).', 'error')
          return
        }
        const blob = new Blob(videoChunksRef.current, { type: mime })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.download = `coloring-video-${Date.now()}.webm`
        a.href = url
        a.click()
        setTimeout(() => URL.revokeObjectURL(url), 1200)
        showToast(lang === 'en' ? 'Video saved automatically.' : 'הווידאו נשמר אוטומטית.', 'success')
      }
      recorder.start(500)
      mediaRecorderRef.current = recorder
      setVideoRecording(true)
      showToast(lang === 'en' ? 'Recording started.' : 'ההקלטה התחילה.', 'info')
    } catch (e) {
      console.error(e)
      showToast('הקלטת וידאו לא נתמכת בדפדפן זה.', 'error')
    }
  }, [outlineSize.w, showToast, lang])

  const stopVideoRecording = useCallback(() => {
    if (mediaRecorderRef.current && videoRecording) {
      mediaRecorderRef.current.stop()
      mediaRecorderRef.current = null
      setVideoRecording(false)
    }
  }, [videoRecording])

  const toggleVideoRecording = useCallback(() => {
    if (videoRecording) stopVideoRecording()
    else startVideoRecording()
  }, [videoRecording, startVideoRecording, stopVideoRecording])

  const addColor = useCallback((hex) => {
    if (typeof hex !== 'string') return
    const normalized = hex.trim().toLowerCase()
    if (!/^#[0-9a-f]{6}$/i.test(normalized)) return
    setColors((c) => (c.includes(normalized) ? c : [...c, normalized]))
  }, [])

  const removeColor = useCallback((hex) => {
    setColors((c) => c.filter((x) => x !== hex))
  }, [])

  useEffect(() => {
    if (!overlayImage || !overlayCanvasRef.current || outlineSize.w === 0) return
    const img = new Image()
    img.onload = () => {
      const ctx = overlayCanvasRef.current.getContext('2d')
      ctx.clearRect(0, 0, outlineSize.w, outlineSize.h)
      ctx.drawImage(img, 0, 0)
    }
    img.src = overlayImage
  }, [overlayImage, outlineSize.w, outlineSize.h])

  // Join room from URL ?room=...
  useEffect(() => {
    if (offlineStrictMode) return
    const params = new URLSearchParams(window.location.search)
    const id = params.get('room')
    if (!id || roomId) return
    let deviceId = deviceIdRef.current
    if (!deviceId && typeof localStorage !== 'undefined') {
      const key = 'colorphoto_device_id'
      deviceId = localStorage.getItem(key)
      if (!deviceId) {
        deviceId = `d-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
        try { localStorage.setItem(key, deviceId) } catch (_) {}
      }
      deviceIdRef.current = deviceId
    }
    const base = (import.meta.env.BASE_URL || '/').replace(/\/?$/, '/')
    const link = `${window.location.origin}${base}?room=${id}`
    window.history.replaceState({}, '', link)
    setRoomId(id)
    setRoomInviteLink(link)
    const registerAndFetch = () =>
      (deviceId ? joinRoom(id, deviceId) : Promise.resolve())
        .then(() => getEvents(id, 0, deviceId))
    registerAndFetch()
      .then(({ events, last_id }) => {
        lastEventIdSeenRef.current = last_id ?? 0
        const snapshot = [...(events || [])].reverse().find((e) => e.type === 'snapshot' && e.payload?.dataUrl)
        if (snapshot?.payload?.dataUrl) loadCompositeIntoCanvas(snapshot.payload.dataUrl)
        else startBlankPage()
        setRoomSyncIssue('')
      })
      .catch(() => {
        setRoomSyncIssue(lang === 'en' ? 'Could not load room; started blank page.' : 'לא הצלחנו לטעון את החדר; נפתח דף ריק.')
        startBlankPage()
      })
  }, [offlineStrictMode, roomId, loadCompositeIntoCanvas, startBlankPage, lang])

  // Restore snapshot image onto canvas when size is ready (high quality)
  useEffect(() => {
    if (!restoreSnapshotUrl || !canvasRef.current || outlineSize.w === 0) return
    const img = new Image()
    img.onload = () => {
      const ctx = canvasRef.current.getContext('2d')
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.clearRect(0, 0, outlineSize.w, outlineSize.h)
      ctx.drawImage(img, 0, 0, outlineSize.w, outlineSize.h)
      setRestoreSnapshotUrl(null)
    }
    img.src = restoreSnapshotUrl
  }, [restoreSnapshotUrl, outlineSize.w, outlineSize.h])

  const getCompositeCanvasRef = useRef(getCompositeCanvas)
  getCompositeCanvasRef.current = getCompositeCanvas

  const getRoomSnapshotDataUrl = useCallback((composite) => {
    const area = (composite?.width || 0) * (composite?.height || 0)
    if (area >= 1400000) return composite.toDataURL('image/jpeg', 0.9)
    return composite.toDataURL('image/png')
  }, [])

  const pushSnapshotToRoom = useCallback(() => {
    if (offlineStrictMode || !roomId || outlineSize.w === 0) return
    const composite = getCompositeCanvasRef.current?.()
    if (!composite) return
    const now = Date.now()
    if (now - lastRoomSnapshotAtRef.current < 4500) return
    const dataUrl = getRoomSnapshotDataUrl(composite)
    const sig = `${dataUrl.length}:${outlineSize.w}x${outlineSize.h}`
    if (sig === lastRoomSnapshotSigRef.current) return
    postEvent(roomId, 'snapshot', {
      dataUrl,
      participantName: participantName || undefined,
    }, deviceIdRef.current)
      .then((r) => {
        if (r?.event_id) lastEventIdSeenRef.current = Math.max(lastEventIdSeenRef.current, r.event_id)
        lastRoomSnapshotAtRef.current = now
        lastRoomSnapshotSigRef.current = sig
        setRoomSyncIssue('')
      })
      .catch(() => {
        setRoomSyncIssue(lang === 'en' ? 'Sync issue: trying to reconnect room updates.' : 'בעיה בסנכרון החדר: מנסים להתחבר מחדש.')
      })
  }, [roomId, outlineSize.w, outlineSize.h, participantName, offlineStrictMode, getRoomSnapshotDataUrl, lang])

  /** מפתח חדש בכל פעם שרוצים לא לשחזר סשן ישן אחרי עדכון אפליקציה */
  const SESSION_RESTORE_KEY = 'colorphoto_session_restore_v4'

  const saveSessionForRestore = useCallback(() => {
    if (outlineSize.w === 0 || !canvasRef.current || typeof sessionStorage === 'undefined') return
    try {
      const canvasDataUrl = canvasRef.current.toDataURL('image/png')
      sessionStorage.setItem(SESSION_RESTORE_KEY, JSON.stringify({
        sessionVersion: 4,
        outlineSize: { w: outlineSize.w, h: outlineSize.h },
        overlayDataUrl: overlayImage || null,
        canvasDataUrl,
        isBlankPage,
      }))
    } catch (_) {}
  }, [outlineSize.w, outlineSize.h, overlayImage, isBlankPage])

  const onStrokeEnd = useCallback(() => {
    if (roomId) {
      if (pushSnapshotTimeoutRef.current) clearTimeout(pushSnapshotTimeoutRef.current)
      pushSnapshotTimeoutRef.current = setTimeout(() => {
        pushSnapshotTimeoutRef.current = null
        pushSnapshotToRoom()
      }, 300)
    } else {
      setTimeout(saveSessionForRestore, 100)
    }
  }, [roomId, pushSnapshotToRoom, saveSessionForRestore])

  const fillPaperBackground = useCallback(() => {
    if (!canvasRef.current || outlineSize.w === 0) return
    const threshold = !effectiveExpertMode || childMode ? 248 : paperFillThreshold
    pushUndo(canvasRef.current.toDataURL('image/png'))
    const hex = getStyledColor(currentColor, paintStyle) || currentColor
    const od = outlineMode === 'lines' ? outlineDataRef.current : null
    fillCanvasPaperBackground(canvasRef.current, hex, od, threshold)
    showToast(
      lang === 'en' ? 'Background filled with selected color' : 'הרקע מולא בצבע הנבחר',
      'info',
    )
    onStrokeEnd()
  }, [
    outlineSize.w,
    effectiveExpertMode,
    childMode,
    paperFillThreshold,
    pushUndo,
    currentColor,
    paintStyle,
    outlineMode,
    lang,
    showToast,
    onStrokeEnd,
  ])

  useEffect(() => {
    if (typeof sessionStorage === 'undefined') return
    const urlRoom = new URLSearchParams(window.location.search).get('room')
    if (urlRoom) return
    try {
      const raw = sessionStorage.getItem(SESSION_RESTORE_KEY)
      if (!raw) return
      const data = JSON.parse(raw)
      if (data?.sessionVersion !== 4 || !data?.outlineSize?.w || !data?.canvasDataUrl) {
        try {
          sessionStorage.removeItem(SESSION_RESTORE_KEY)
        } catch (_) {}
        return
      }
      setOverlayImage(data.overlayDataUrl || null)
      setIsBlankPage(!!data.isBlankPage)
      setEraserBaseImageUrl(data.canvasDataUrl)
      setOutlineSize(data.outlineSize)
      setUndoStack([])
      setRedoStack([])
      setRestoreSnapshotUrl(data.canvasDataUrl)
      if (data.overlayDataUrl) {
        const img = new Image()
        img.onload = () => {
          const c = document.createElement('canvas')
          c.width = data.outlineSize.w
          c.height = data.outlineSize.h
          const ctx = c.getContext('2d')
          ctx.drawImage(img, 0, 0)
          const id = ctx.getImageData(0, 0, c.width, c.height)
          const out = new ImageData(c.width, c.height)
          for (let i = 0; i < id.data.length; i += 4) {
            const a = id.data[i + 3]
            const v = a > 128 ? 0 : 255
            out.data[i] = out.data[i + 1] = out.data[i + 2] = v
            out.data[i + 3] = 255
          }
          outlineDataRef.current = out
        }
        img.src = data.overlayDataUrl
      }
    } catch (_) {}
  }, [])

  useEffect(() => {
    if (participantName && typeof localStorage !== 'undefined') localStorage.setItem('coloring_participant_name', participantName)
  }, [participantName])

  useEffect(() => {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem('colorphoto_sensitive_color_press', sensitiveColorPress ? '1' : '0')
  }, [sensitiveColorPress])

  useEffect(() => {
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem('colorphoto_offline_strict', offlineStrictMode ? '1' : '0')
    } catch {
      // ignore
    }
  }, [offlineStrictMode])

  const openNamePrompt = useCallback(() => {
    showPromptToast(
      lang === 'en' ? 'Your name (shown in shared room)' : 'איך לקרוא לך? (להצגה בחדר ציור משותף)',
      (participantName || '').trim(),
      (name) => {
        const val = (name || '').trim() || (lang === 'en' ? 'User' : 'משתמש')
        setParticipantName(val)
        if (typeof localStorage !== 'undefined') localStorage.setItem('coloring_participant_name', val)
      },
      undefined,
      lang === 'en' ? 'OK' : 'שמור',
      lang === 'en' ? 'Cancel' : 'ביטול'
    )
  }, [participantName, showPromptToast, lang])

  // Periodic backup snapshot to room.
  useEffect(() => {
    if (offlineStrictMode || !roomId || outlineSize.w === 0) return
    const t = setInterval(pushSnapshotToRoom, 15000)
    return () => clearInterval(t)
  }, [roomId, outlineSize.w, pushSnapshotToRoom, offlineStrictMode])

  // Live sync: adaptive polling to reduce load and recover gracefully on network issues.
  useEffect(() => {
    if (offlineStrictMode || !roomId || outlineSize.w === 0) return
    let disposed = false
    let timer = null
    const poll = () => {
      if (disposed) return
      getEvents(roomId, lastEventIdSeenRef.current, deviceIdRef.current)
        .then(({ events, last_id }) => {
          if (last_id != null) lastEventIdSeenRef.current = last_id
          const newEvents = (events || []).map((e) => ({
            id: e.id,
            type: e.type,
            participantName: e.payload?.participantName || 'מישהו',
            created_at: e.created_at,
          }))
          if (newEvents.length) {
            setRoomLiveEvents((prev) => {
              const byId = new Map(prev.map((p) => [p.id, p]))
              newEvents.forEach((e) => byId.set(e.id, e))
              return Array.from(byId.values()).sort((a, b) => b.id - a.id).slice(0, 25)
            })
          }
          const snapshots = (events || []).filter((e) => e.type === 'snapshot' && e.payload?.dataUrl)
          const latest = snapshots[snapshots.length - 1]
          if (latest?.payload?.dataUrl) {
            const from = latest.payload.participantName
            if (from && from !== (participantName || '').trim()) setLiveUpdateFrom(from)
            loadCompositeIntoCanvas(latest.payload.dataUrl)
          }
          setRoomSyncIssue('')
          roomPollDelayRef.current = events?.length ? 900 : Math.min(2600, Math.max(1200, roomPollDelayRef.current + 180))
        })
        .catch(() => {
          roomPollDelayRef.current = Math.min(6000, Math.max(1700, roomPollDelayRef.current * 1.6))
          setRoomSyncIssue(lang === 'en' ? 'Sync issue: trying to reconnect room updates.' : 'בעיה בסנכרון החדר: מנסים להתחבר מחדש.')
        })
        .finally(() => {
          if (!disposed) timer = setTimeout(poll, roomPollDelayRef.current)
        })
    }
    roomPollDelayRef.current = 1200
    poll()
    return () => {
      disposed = true
      if (timer) clearTimeout(timer)
    }
  }, [roomId, outlineSize.w, loadCompositeIntoCanvas, participantName, offlineStrictMode, lang])

  useEffect(() => {
    if (!roomId || roomLiveEvents.length === 0) return
    const t = setInterval(() => setRoomLiveEvents((prev) => (prev.length ? [...prev] : [])), 2000)
    return () => clearInterval(t)
  }, [roomId, roomLiveEvents.length])

  useEffect(() => {
    if (!liveUpdateFrom) return
    const t = setTimeout(() => setLiveUpdateFrom(null), 3000)
    return () => clearTimeout(t)
  }, [liveUpdateFrom])

  // Audit: unique device count + device id for room validation (once on load)
  useEffect(() => {
    if (offlineStrictMode) return
    const key = 'colorphoto_device_id'
    let deviceId = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null
    if (!deviceId) {
      deviceId = `d-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
      try { localStorage.setItem(key, deviceId) } catch (_) {}
    }
    deviceIdRef.current = deviceId
    auditDevice(deviceId).then((n) => { if (n != null) setUniqueDeviceCount(n) }).catch(() => {})
  }, [offlineStrictMode])

  // Before leaving: confirm if there is work; mention possible auto-save
  useEffect(() => {
    const msg = 'ייתכן שיש שמירה אוטומטית של מה שנעשה. האם לבצע רענון/לצאת?'
    const handler = (e) => {
      if (outlineSize.w > 0) {
        e.preventDefault()
        e.returnValue = msg
        return msg
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [outlineSize.w])

  const mixColors = useCallback((hex1, hex2, ratio = 0.5) => {
    const isHex = (v) => typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v.trim())
    if (!isHex(hex1) || !isHex(hex2)) return '#000000'
    const safeRatio = Number.isFinite(ratio) ? Math.max(0, Math.min(1, ratio)) : 0.5
    const parse = (h) => {
      const n = h.trim().replace('#', '')
      return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)]
    }
    const [r1, g1, b1] = parse(hex1)
    const [r2, g2, b2] = parse(hex2)
    const r = Math.round(r1 * (1 - safeRatio) + r2 * safeRatio)
    const g = Math.round(g1 * (1 - safeRatio) + g2 * safeRatio)
    const b = Math.round(b1 * (1 - safeRatio) + b2 * safeRatio)
    const toHex = (x) => x.toString(16).padStart(2, '0')
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
  }, [])

  const drawDataUrlToContext = useCallback((ctx, dataUrl, w, h) => {
    if (!ctx || !dataUrl || !w || !h) return Promise.resolve()
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        try {
          ctx.drawImage(img, 0, 0, w, h)
        } catch {
          // ignore draw failures for broken layer data
        }
        resolve()
      }
      img.onerror = () => resolve()
      img.src = dataUrl
    })
  }, [])

  const persistActiveLayer = useCallback(() => {
    if (!canvasRef.current || !activeLayerId) return
    let snapshot = null
    try {
      snapshot = canvasRef.current.toDataURL('image/png')
    } catch {
      snapshot = null
    }
    if (!snapshot) return
    setLayers((prev) => prev.map((layer) => (
      layer.id === activeLayerId ? { ...layer, dataUrl: snapshot } : layer
    )))
  }, [activeLayerId])

  const renderUnderlayLayers = useCallback(async (nextLayers = layers, nextActiveId = activeLayerId) => {
    const canvas = layersUnderlayRef.current
    if (!canvas || !outlineSize.w || !outlineSize.h) return
    canvas.width = outlineSize.w
    canvas.height = outlineSize.h
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, outlineSize.w, outlineSize.h)
    for (const layer of nextLayers) {
      if (!layer.visible || layer.id === nextActiveId) continue
      if (!layer.dataUrl) continue
      // Draw visible non-active layers under the editable active layer.
      // eslint-disable-next-line no-await-in-loop
      await drawDataUrlToContext(ctx, layer.dataUrl, outlineSize.w, outlineSize.h)
    }
  }, [layers, activeLayerId, outlineSize.w, outlineSize.h, drawDataUrlToContext])

  const loadActiveLayerToCanvas = useCallback(async (nextLayers = layers, nextActiveId = activeLayerId) => {
    if (!canvasRef.current || !outlineSize.w || !outlineSize.h) return
    const layer = nextLayers.find((l) => l.id === nextActiveId)
    if (!layer) return
    const ctx = canvasRef.current.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, outlineSize.w, outlineSize.h)
    if (layer.dataUrl) {
      await drawDataUrlToContext(ctx, layer.dataUrl, outlineSize.w, outlineSize.h)
      return
    }
    // Keep first layer as white paper; additional layers start transparent.
    if (layer.id === 'layer-1') {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, outlineSize.w, outlineSize.h)
    }
  }, [layers, activeLayerId, outlineSize.w, outlineSize.h, drawDataUrlToContext])

  useEffect(() => {
    if (!outlineSize.w || !outlineSize.h) return
    renderUnderlayLayers()
  }, [layers, activeLayerId, outlineSize.w, outlineSize.h, renderUnderlayLayers])

  useEffect(() => {
    if (!outlineSize.w || !outlineSize.h) return
    loadActiveLayerToCanvas()
  }, [activeLayerId, outlineSize.w, outlineSize.h, loadActiveLayerToCanvas])

  const createLayer = useCallback(() => {
    persistActiveLayer()
    const id = `layer-${Date.now()}`
    const name = `Layer ${layers.length + 1}`
    const nextLayers = [...layers, { id, name, visible: true, dataUrl: null }]
    setLayers(nextLayers)
    setActiveLayerId(id)
  }, [persistActiveLayer, layers])

  const switchActiveLayer = useCallback((layerId) => {
    if (!layerId || layerId === activeLayerId) return
    persistActiveLayer()
    setActiveLayerId(layerId)
  }, [activeLayerId, persistActiveLayer])

  const toggleLayerVisibility = useCallback((layerId) => {
    if (layerId === activeLayerId) persistActiveLayer()
    setLayers((prev) => {
      const target = prev.find((l) => l.id === layerId)
      if (!target) return prev
      const next = prev.map((l) => (
        l.id === layerId ? { ...l, visible: !l.visible } : l
      ))
      if (layerId === activeLayerId && target.visible) {
        const firstVisible = next.find((l) => l.id !== layerId && l.visible)
        if (firstVisible) setActiveLayerId(firstVisible.id)
      }
      return next
    })
  }, [activeLayerId, persistActiveLayer])

  const closeDrawer = useCallback(() => setMobileDrawerOpen(false), [])

  const toggleAccessibility = useCallback(() => {
    setAccessibilityMode((v) => !v)
  }, [])

  useEffect(() => {
    if (accessibilityMode) document.body.classList.add('accessibility-mode-on')
    else document.body.classList.remove('accessibility-mode-on')
    return () => document.body.classList.remove('accessibility-mode-on')
  }, [accessibilityMode])

  useEffect(() => {
    if (darkMode) document.body.classList.add('theme-dark')
    else document.body.classList.remove('theme-dark')
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem('colorphoto_dark_mode', darkMode ? '1' : '0')
    } catch {}
    const metaTheme = document.querySelector('meta[name="theme-color"]')
    if (metaTheme) metaTheme.setAttribute('content', darkMode ? '#0f1419' : '#f6f8fb')
    return () => document.body.classList.remove('theme-dark')
  }, [darkMode])

  const toggleReadAloud = useCallback(() => {
    setReadAloudMode((v) => !v)
    if (readAloudMode) cancelSpeech()
  }, [readAloudMode])

  const toggleLang = useCallback(() => {
    setLang((prev) => {
      const next = prev === 'he' ? 'en' : 'he'
      saveLang(next)
      return next
    })
  }, [])

  useEffect(() => {
    if (!readAloudMode) return
    const onDocClick = (e) => {
      const target = e.target
      if (target.closest?.('.read-aloud-toggle, .accessibility-toggle')) return
      const text = getTextToSpeak(target)
      if (text) speakHebrew(text)
    }
    document.addEventListener('click', onDocClick, true)
    return () => document.removeEventListener('click', onDocClick, true)
  }, [readAloudMode])

  const toggleAbout = useCallback(() => {
    setAboutOpen((v) => !v)
  }, [])

  const searchSuggestions = ['חיות לצביעה', 'דמויות מצוירות', 'פרחים', 'רכבים', 'דינוזאורים', 'נסיכות']

  const setMode = useCallback((mode) => {
    setUserMode(mode)
    if (typeof localStorage !== 'undefined') localStorage.setItem('colorphoto_user_mode', mode)
  }, [])

  const chooseStartupLanguage = useCallback((nextLang) => {
    const safeLang = nextLang === 'en' ? 'en' : 'he'
    setLang(safeLang)
    saveLang(safeLang)
    setLanguageGateOpen(false)
  }, [])

  const measurementDistancePx = useMemo(() => {
    if (measurementPoints.length < 2) return 0
    const [a, b] = measurementPoints
    return Math.hypot((b.x - a.x), (b.y - a.y))
  }, [measurementPoints])

  const measurementCmPerPx = useMemo(() => {
    const safePx = Math.max(1, Number(measurementCalibrationPx) || 1)
    const safeCm = Math.max(0.01, Number(measurementCalibrationCm) || 0.01)
    return safeCm / safePx
  }, [measurementCalibrationCm, measurementCalibrationPx])

  const measurementDistanceCm = useMemo(
    () => measurementDistancePx * measurementCmPerPx,
    [measurementDistancePx, measurementCmPerPx],
  )
  const measurementDistanceInch = useMemo(() => measurementDistanceCm / 2.54, [measurementDistanceCm])
  const measurementWidthCm = useMemo(() => {
    if (measurementPoints.length < 2) return 0
    const [a, b] = measurementPoints
    return Math.abs(b.x - a.x) * measurementCmPerPx
  }, [measurementPoints, measurementCmPerPx])
  const measurementWidthInch = useMemo(() => measurementWidthCm / 2.54, [measurementWidthCm])
  const measurementHeightCm = useMemo(() => {
    if (measurementPoints.length < 2) return 0
    const [a, b] = measurementPoints
    return Math.abs(b.y - a.y) * measurementCmPerPx
  }, [measurementPoints, measurementCmPerPx])
  const measurementHeightInch = useMemo(() => measurementHeightCm / 2.54, [measurementHeightCm])

  const handleMeasurePoint = useCallback((point) => {
    setMeasurementPoints((prev) => (prev.length >= 2 ? [point] : [...prev, point]))
  }, [])

  const captureForMeasurement = useCallback(async () => {
    setMeasurementPoints([])
    setTool('measure')
    await openCamera()
    showToast(
      lang === 'en'
        ? 'Photo loaded. Tap two points to measure width and height.'
        : 'התמונה נטענה. לחצו על שתי נקודות למדידה של רוחב וגובה.',
      'info',
    )
  }, [openCamera, setTool, showToast, lang])

  const applyAdultPreset = useCallback((preset) => {
    setAdultPreset(preset)
    if (preset === 'tattoo') {
      setPaintStyle('matte')
      setTool('pencil')
      setBrushSize(8)
      setSplitMode(true)
      setGridCells(null)
      setTriangleOverlay(false)
      setLineAssistEnabled(true)
      setLineAssistStrength(0.8)
    } else {
      setPaintStyle('normal')
      setTool('brush')
      setBrushSize(14)
      setSplitMode(false)
      setGridCells({ rows: 3, cols: 3 })
      setTriangleOverlay(false)
      setLineAssistEnabled(true)
      setLineAssistStrength(0.68)
    }
  }, [setTool])

  if (languageGateOpen) {
    return (
      <div className="app welcome welcome-language-gate">
        <header className="welcome-header welcome-header-simple">
          <div className="welcome-hero">
            <h1>Choose language / בחרו שפה</h1>
            <p className="welcome-tagline">בחרו שפה לפני תחילת השימוש</p>
          </div>
        </header>
        <div className="welcome-choose-mode-buttons welcome-language-buttons" role="group" aria-label="Language selection">
          <button
            type="button"
            className="welcome-choose-mode-btn companion"
            onClick={() => chooseStartupLanguage('en')}
          >
            <span className="welcome-choose-mode-label">English</span>
          </button>
          <button
            type="button"
            className="welcome-choose-mode-btn child"
            onClick={() => chooseStartupLanguage('he')}
          >
            <span className="welcome-choose-mode-label">עברית</span>
          </button>
        </div>
      </div>
    )
  }

  if (userMode === null) {
    return (
      <div className="app welcome welcome-choose-mode">
        <header className="welcome-header welcome-header-simple">
          <div className="welcome-hero">
            <div className="welcome-cover-wrap">
              <img
                src={`${(import.meta.env.BASE_URL || '/').replace(/\/?$/, '/')}cover.png`}
                alt="אפליקציית צביעה לילדים"
                className="welcome-cover"
                loading="eager"
                decoding="async"
              />
            </div>
            <div className="welcome-cover-actions-center" role="group" aria-label="בחירת מצב">
              <div className="welcome-cover-actions-block">
                <button
                  type="button"
                  className="welcome-drawer-btn"
                  onClick={() => setMode('child')}
                >
                  לילד
                </button>
                <button
                  type="button"
                  className="welcome-drawer-btn"
                  onClick={() => setMode('companion')}
                >
                  ילד + מלווה
                </button>
                <button
                  type="button"
                  className="welcome-drawer-btn"
                  onClick={() => setMode('adult')}
                >
                  למבוגרים
                </button>
              </div>
            </div>
            <h1>דפי צביעה</h1>
            <p className="welcome-tagline">למי האפליקציה?</p>
          </div>
        </header>
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    )
  }

  if (outlineSize.w === 0) {
    return (
      <div className={`app welcome ${accessibilityMode ? 'accessibility-on' : ''}`}>
        {imageSearchModalOpen && (
          <div
            className="image-search-modal-overlay"
            onClick={() => setImageSearchModalOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="תוצאות חיפוש תמונות"
          >
            <div className="image-search-modal" onClick={(e) => e.stopPropagation()}>
              <div className="image-search-modal-header">
                <div className="image-search-modal-title">בחרו תמונה</div>
                <button type="button" className="image-search-modal-close" onClick={() => setImageSearchModalOpen(false)} aria-label="סגור תפריט">
                  <IconClose size={22} />
                </button>
              </div>
              {imageSearchError && <div className="welcome-search-error">{imageSearchError}</div>}
              <div className="image-search-modal-grid" role="list">
                {imageSearchResults.map((r) => (
                  <button
                    key={r.fullUrl}
                    type="button"
                    className="image-search-modal-item"
                    onClick={async () => {
                      setImageSearchModalOpen(false)
                      await importImageFromUrl(r.fullUrl, r.title)
                    }}
                    title={`ייבוא: ${r.title}`}
                    aria-label={`ייבוא תמונה: ${r.title}`}
                  >
                    <img src={r.thumbUrl} alt={r.title} loading="lazy" />
                  </button>
                ))}
              </div>
              <div className="image-search-modal-hint">בחרו תמונה - בלחיצה היא תתווסף לאפליקציה לצביעה.</div>
              <div className="image-search-modal-google">
                <button type="button" className="welcome-search-secondary-btn" onClick={() => openGoogleImageSearch(imageSearchQuery)}>
                  חפש גם בגוגל תמונות
                </button>
              </div>
            </div>
          </div>
        )}
        <header className="welcome-header welcome-header-simple">
          <div className="welcome-hero">
            <div className="welcome-cover-wrap">
              <img
                src={`${(import.meta.env.BASE_URL || '/').replace(/\/?$/, '/') }cover.png`}
                alt="אפליקציית צביעה לילדים"
                className="welcome-cover"
                loading="eager"
                decoding="async"
              />
              <div className="welcome-cover-actions-center welcome-cover-actions-center-start" role="group" aria-label="אפשרויות התחלה">
                {childMode ? (
                  <div className="welcome-cover-actions-block">
                    <div className="welcome-source-grid welcome-source-grid-oncover">
                      <button type="button" className="welcome-source-btn" onPointerDown={spawnStarTrail} onClick={() => void triggerMainImageUpload()} title="העלאה מקובץ">
                        <span className="welcome-source-icon">⬆️</span>
                        <span className="welcome-source-label">העלאה מקובץ</span>
                      </button>
                      <button type="button" className="welcome-source-btn" onPointerDown={spawnStarTrail} onClick={() => void openCamera()} title="צילום במצלמה">
                        <span className="welcome-source-icon">📷</span>
                        <span className="welcome-source-label">צילום</span>
                      </button>
                    </div>
                    <div className="welcome-child-inline welcome-child-inline-oncover">
                      <div className="welcome-drawer-section">
                        <button type="button" className="welcome-drawer-btn" onPointerDown={spawnStarTrail} onClick={() => startBlankPage({ w: 800, h: 600 })}>דף ריק</button>
                        <button type="button" className="welcome-drawer-btn" onPointerDown={spawnStarTrail} onClick={() => openGallery()}>גלריה</button>
                        <button type="button" className="welcome-drawer-btn" onPointerDown={spawnStarTrail} onClick={() => setTemplatesGalleryOpen(true)}>תבניות לצביעה</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="welcome-cover-actions-block">
                    <div className="welcome-drawer-section">
                      <button type="button" className="welcome-drawer-btn" onClick={() => setWelcomeMenuOpen(true)}>תפריט</button>
                      <button type="button" className="welcome-drawer-btn" onClick={() => setTemplatesGalleryOpen(true)}>תבניות לצביעה</button>
                      <button type="button" className="welcome-drawer-btn" onClick={() => openGallery()}>גלריה</button>
                      <button type="button" className="welcome-drawer-btn" onClick={() => startBlankPage()}>דף ריק</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <h1>דפי צביעה</h1>
            <p className="welcome-tagline">{childMode ? 'בחרו תמונה או דף ריק' : 'בחרו אפשרות מהתפריט'}</p>
          </div>
          <img
            className="welcome-side-brand"
            src={COLORDEV_SIDE_LOGO_DATA_URL}
            alt="Colordev"
            loading="lazy"
            decoding="async"
          />
          {!childMode && (
            <button
              type="button"
              className="welcome-menu-toggle"
              onClick={() => setWelcomeMenuOpen(true)}
              aria-label="תפריט"
              aria-expanded={welcomeMenuOpen}
            >
              <span className="welcome-menu-toggle-icon" aria-hidden><IconBearFlag size={26} /></span>
              <span className="welcome-menu-toggle-label">תפריט</span>
            </button>
          )}
        </header>

        <div
          className="welcome-drawer-overlay"
          data-open={welcomeMenuOpen}
          onClick={() => setWelcomeMenuOpen(false)}
          aria-hidden={!welcomeMenuOpen}
        />
        <div className="welcome-drawer-panel" data-open={welcomeMenuOpen}>
          <div className="welcome-drawer-header">
            <h2>תפריט</h2>
            <button type="button" className="drawer-close" onClick={() => setWelcomeMenuOpen(false)} aria-label="סגור תפריט">
              <IconClose size={22} />
            </button>
          </div>
          <div className="welcome-drawer-body">
            {!childMode && (
              <div className="welcome-drawer-section">
                <span className="welcome-drawer-section-title">{lang === 'en' ? 'Network mode' : 'מצב רשת'}</span>
                <label className="drawer-option-label">
                  <input
                    type="checkbox"
                    checked={offlineStrictMode}
                    onChange={() => setOfflineStrictMode((v) => !v)}
                  />
                  <span>{lang === 'en' ? 'Strict offline (no network)' : 'אופליין מחמיר (בלי רשת)'}</span>
                </label>
                <p className="welcome-drawer-hint">
                  {lang === 'en'
                    ? 'Off: image search, shared rooms, and share-link use the internet.'
                    : 'כבוי: חיפוש תמונות, חדרים משותפים ושיתוף קישור דורשים אינטרנט.'}
                </p>
              </div>
            )}
            <div className="welcome-drawer-section">
              <span className="welcome-drawer-section-title">בחרו איך להוסיף תמונה</span>
              <p className="welcome-drawer-hint">{childMode ? 'צילום במצלמה או העלאת קובץ' : 'איך תרצו להתחיל? בחרו מקור אחד.'}</p>
              <div className="welcome-source-grid">
                <button type="button" className="welcome-source-btn" onClick={() => { setWelcomeMenuOpen(false); void triggerMainImageUpload(); }} title="העלאה מקובץ">
                  <span className="welcome-source-icon"><i className="fa-solid fa-upload" aria-hidden /></span>
                  <span className="welcome-source-label">העלאה מקובץ</span>
                </button>
                <button type="button" className="welcome-source-btn" onClick={() => { setWelcomeMenuOpen(false); void openCamera(); }} title="צילום במצלמה">
                  <span className="welcome-source-icon"><i className="fa-solid fa-camera" aria-hidden /></span>
                  <span className="welcome-source-label">צילום</span>
                </button>
                {!childMode && (
                  <>
                    <button type="button" className="welcome-source-btn" onClick={() => { pasteImageFromClipboard(); setWelcomeMenuOpen(false); }} title="הדבק תמונה מהלוח (Ctrl+V)">
                      <span className="welcome-source-icon"><i className="fa-solid fa-paste" aria-hidden /></span>
                      <span className="welcome-source-label">הדבק תמונה</span>
                    </button>
                    <button
                      type="button"
                      className="welcome-source-btn welcome-source-search"
                      disabled={offlineStrictMode}
                      onClick={() => { setImageSearchModalOpen(true); setWelcomeMenuOpen(false); }}
                      title={offlineStrictMode ? (lang === 'en' ? 'Disabled in strict offline' : 'כבוי במצב אופליין מחמיר') : 'חפש תמונה באינטרנט'}
                    >
                      <span className="welcome-source-icon"><i className="fa-solid fa-magnifying-glass" aria-hidden /></span>
                      <span className="welcome-source-label">חיפוש תמונה</span>
                    </button>
                  </>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden-input" aria-hidden tabIndex={-1} />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleCameraFileSelect} className="hidden-input" aria-hidden tabIndex={-1} />
            </div>

            {!childMode && (
            <div className="welcome-drawer-section welcome-drawer-section-search" id="welcome-search-inline">
              <span className="welcome-drawer-section-title">חיפוש תמונה (פירוט)</span>
              <div className="welcome-search-row">
                <input
                  type="text"
                  className="welcome-search-input"
                  placeholder="מה לחפש..."
                  value={imageSearchQuery}
                  disabled={offlineStrictMode}
                  onChange={(e) => setImageSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !offlineStrictMode && (openGoogleImageSearch() || searchImages())}
                />
                <button type="button" className="primary welcome-drawer-btn-sm" onClick={() => openGoogleImageSearch()} disabled={offlineStrictMode}>גוגל תמונות</button>
              </div>
              <div className="welcome-search-suggestions">
                {searchSuggestions.map((s) => (
                  <button key={s} type="button" className="welcome-suggestion-btn" disabled={offlineStrictMode} onClick={() => { setImageSearchQuery(s); openGoogleImageSearch(s); }}>{s}</button>
                ))}
              </div>
              <button type="button" className="welcome-drawer-btn-sm" onClick={() => searchImages()} disabled={imageSearchLoading || offlineStrictMode}>
                {imageSearchLoading ? 'מחפש…' : 'חפש כאן במאגר תמונות'}
              </button>
              {imageSearchError && <div className="welcome-search-error">{imageSearchError}</div>}
            </div>
            )}

            <div className="welcome-drawer-section">
              <span className="welcome-drawer-section-title">{childMode ? 'או' : 'אפשרויות נוספות'}</span>
              {!childMode && <p className="welcome-drawer-hint">מידות לדף ריק (בפיקסלים):</p>}
              {!childMode && (
              <div className="welcome-blank-sizes">
                {[
                  { w: 800, h: 600, label: '800×600' },
                  { w: 1024, h: 768, label: '1024×768' },
                  { w: 1920, h: 1080, label: '1920×1080' },
                  { w: 2480, h: 3508, label: 'A4' },
                ].map(({ w, h, label }) => (
                  <button
                    key={label}
                    type="button"
                    className={`welcome-size-btn ${blankPageSize.w === w && blankPageSize.h === h ? 'selected' : ''}`}
                    onClick={() => setBlankPageSize({ w, h })}
                  >
                    {label}
                  </button>
                ))}
              </div>
              )}
              <button type="button" className="welcome-drawer-btn" onClick={() => { startBlankPage(childMode ? { w: 800, h: 600 } : undefined); setWelcomeMenuOpen(false); }}><i className="fa-solid fa-image" aria-hidden /> דף ריק {childMode ? '' : `(${blankPageSize.w}×${blankPageSize.h})`}</button>
              <button type="button" className="welcome-drawer-btn" onClick={() => { openGallery(); setWelcomeMenuOpen(false); }}><i className="fa-solid fa-images" aria-hidden /> גלריה</button>
              <button type="button" className="welcome-drawer-btn" onClick={() => { setTemplatesGalleryOpen(true); setWelcomeMenuOpen(false); }}><i className="fa-solid fa-shapes" aria-hidden /> תבניות לצביעה</button>
            </div>

            {!childMode && (
            <>
            <div className="welcome-drawer-section">
              <span className="welcome-drawer-section-title">הרכיבי מילה</span>
              <p className="welcome-compose-hint">כתבי שם או מילה - יופיעו באותיות בלון יפות לצביעה.</p>
              <div className="welcome-compose-row">
                <input
                  type="text"
                  className="welcome-compose-input"
                  placeholder="למשל: השם שלי, שלום, אבא"
                  value={composeWord}
                  onChange={(e) => setComposeWord(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && openWordPage()}
                  dir="auto"
                />
                <button type="button" className="primary welcome-compose-btn" onClick={() => openWordPage()}>
                  צור דף צביעה
                </button>
              </div>
            </div>

            <div className="welcome-drawer-section">
              <span className="welcome-drawer-section-title">דפי צביעה מוכנים (אותיות ומספרים)</span>
              <button type="button" className="welcome-drawer-btn welcome-abc-one-page" onClick={() => { openAlphabetGridPage('hebrew'); setWelcomeMenuOpen(false); }}>
                <i className="fa-solid fa-font" aria-hidden /> דף אלף-בית — כל האותיות העבריות
              </button>
              <button type="button" className="welcome-drawer-btn welcome-abc-one-page" onClick={() => { openAlphabetGridPage('latin'); setWelcomeMenuOpen(false); }}>
                <i className="fa-solid fa-font" aria-hidden /> דף ABC — אותיות באנגלית
              </button>
              <div className="welcome-default-pages-grid welcome-drawer-chars">
                {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((c) => (
                  <button key={c} type="button" className="welcome-default-char-btn" onClick={() => { openDefaultColoringPage(c); setWelcomeMenuOpen(false); }}>{c}</button>
                ))}
              </div>
              <div className="welcome-default-pages-grid welcome-drawer-chars">
                {HEBREW_LETTERS.map((c) => (
                  <button key={c} type="button" className="welcome-default-char-btn" onClick={() => { openDefaultColoringPage(c); setWelcomeMenuOpen(false); }}>{c}</button>
                ))}
              </div>
              <div className="welcome-default-pages-grid welcome-drawer-chars">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map((c) => (
                  <button key={c} type="button" className="welcome-default-char-btn" onClick={() => { openDefaultColoringPage(c); setWelcomeMenuOpen(false); }}>{c}</button>
                ))}
              </div>
            </div>

            <div className="welcome-drawer-section">
              <button
                type="button"
                className="welcome-drawer-btn welcome-btn-room"
                onClick={() => { createRoomAndInvite(); setWelcomeMenuOpen(false); }}
                disabled={roomCreating || offlineStrictMode}
                title={offlineStrictMode ? (lang === 'en' ? 'Turn off strict offline in Settings' : 'כבו מצב אופליין מחמיר בהגדרות') : undefined}
              >
                {roomCreating ? 'פותח חדר…' : <><i className="fa-solid fa-users" aria-hidden /> פתח חדר ציור והזמן חברים</>}
              </button>
            </div>
            <div className="welcome-drawer-section">
              <button type="button" className="welcome-drawer-btn welcome-drawer-btn-admin" onClick={() => { setAdminOpen(true); setWelcomeMenuOpen(false); }}>
                ניהול קטגוריות
              </button>
            </div>
            </>
            )}
          </div>
        </div>

        {adminOpen && (
          <Suspense fallback={null}>
            <AdminCategories onClose={() => setAdminOpen(false)} />
          </Suspense>
        )}
        {roomModalOpen && roomInviteLink && (
          <div className="room-invite-overlay" onClick={() => setRoomModalOpen(false)} role="dialog" aria-modal="true" aria-label="הזמן חברים לחדר">
            <div className="room-invite-modal" onClick={(e) => e.stopPropagation()}>
              <div className="room-invite-header">
                <h3>החדר פתוח - שתפו את הקישור</h3>
                <button type="button" className="room-invite-close" onClick={() => setRoomModalOpen(false)} aria-label="סגור"><IconClose size={22} /></button>
              </div>
              <p className="room-invite-hint">שלחו את הקישור לחברים כדי שיוכלו להצטרף ולצבוע איתכם. הציור מתעדכן בחדר (live).</p>
              <div className="room-invite-name-row">
                <label htmlFor="room-participant-name-modal">שם משתתף</label>
                <input id="room-participant-name-modal" type="text" className="room-invite-name-input" placeholder="השם שיוצג כשתעדכן את הציור" value={participantName || ''} onChange={(e) => setParticipantName(e.target.value)} />
              </div>
              <div className="room-invite-link-row">
                <input type="text" className="room-invite-input" readOnly value={roomInviteLink} />
                <button type="button" className="primary" onClick={shareRoomLink}>שתף קישור</button>
                <button type="button" className="room-invite-copy" onClick={copyRoomLink}>העתק</button>
              </div>
              {roomSyncIssue && <p className="room-invite-hint">{roomSyncIssue}</p>}
            </div>
          </div>
        )}

        {galleryOpen && (
          <Gallery
            items={galleryItems}
            onClose={() => setGalleryOpen(false)}
            onItemsChange={() => setGalleryItems(getGallery())}
            onOpenForEdit={(item) => loadCompositeIntoCanvas(item.dataUrl)}
            lang={lang}
          />
        )}
        {templatesGalleryOpen && (
          <Suspense fallback={null}>
            <TemplatesGallery
              onSelect={applyTemplate}
              onClose={() => setTemplatesGalleryOpen(false)}
              onSelectCustomImage={(url) => { void openTemplateImageAsColoringPage(url) }}
              childMode={childMode}
            />
          </Suspense>
        )}
        {uniqueDeviceCount != null && !welcomeMenuOpen && !roomModalOpen && !galleryOpen && !templatesGalleryOpen && (
          <div className="welcome-footer-audit" title="מכשירים ייחודיים שהשתמשו באפליקציה">
            <span className="app-footer-audit-icon"><IconEye size={16} /></span>
            <span className="app-footer-audit-count">{uniqueDeviceCount}</span>
          </div>
        )}
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    )
  }

  return (
    <div ref={appRootRef} className={`app workspace ${(outlineSize.w > 0 || isBlankPage) ? 'painting-active' : ''} ${accessibilityMode ? 'accessibility-on' : ''} ${childMode ? 'child-mode' : ''} ${adultMode ? 'adult-mode' : ''}`}>
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden-input" aria-hidden tabIndex={-1} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleCameraFileSelect} className="hidden-input" aria-hidden tabIndex={-1} />
      {mainMenuOverlayOpen && (
        <div className="main-menu-overlay" role="dialog" aria-modal="true" aria-label="תפריט ראשי">
          <div className="main-menu-overlay-backdrop" onClick={() => setMainMenuOverlayOpen(false)} />
          <div className="main-menu-overlay-panel">
            <div className="drawer-panel-header">
              <h2>תפריט</h2>
              <button type="button" className="drawer-close" onClick={() => setMainMenuOverlayOpen(false)} aria-label="סגור">
                <IconClose size={22} />
              </button>
            </div>
            <div className="drawer-panel-body">
              <details className="welcome-drawer-section">
                <summary className="welcome-drawer-section-title">{lang === 'en' ? 'Settings' : 'הגדרות'}</summary>
                <div className="drawer-option-row">
                  <label className="drawer-option-label">
                    <input type="checkbox" checked={darkMode} onChange={() => setDarkMode((v) => !v)} />
                    <span>{lang === 'en' ? 'Dark mode' : 'מצב כהה'}</span>
                  </label>
                </div>
                <div className="drawer-option-row">
                  <label className="drawer-option-label">
                    <input type="checkbox" checked={lang === 'en'} onChange={toggleLang} />
                    <span>{lang === 'en' ? 'English' : 'אנגלית'}</span>
                  </label>
                </div>
                <div className="drawer-option-row">
                  <label className="drawer-option-label">
                    <input type="checkbox" checked={readAloudMode} onChange={toggleReadAloud} />
                    <span>{lang === 'en' ? 'Read aloud' : 'הקראה'}</span>
                  </label>
                </div>
                <div className="drawer-option-row">
                  <label className="drawer-option-label">
                    <input type="checkbox" checked={accessibilityMode} onChange={toggleAccessibility} />
                    <span>{lang === 'en' ? 'Accessibility' : 'נגישות'}</span>
                  </label>
                </div>
                <div className="drawer-option-row">
                  <label className="drawer-option-label">
                    <input
                      type="checkbox"
                      checked={offlineStrictMode}
                      onChange={() => setOfflineStrictMode((v) => !v)}
                    />
                    <span>{lang === 'en' ? 'Strict offline (no network)' : 'מצב אופליין מחמיר (בלי רשת)'}</span>
                  </label>
                  <p className="drawer-option-desc">
                    {lang === 'en'
                      ? 'Off: image search, shared rooms, share link, QR, and analytics may use the network.'
                      : 'כבוי: חיפוש תמונות, חדרים משותפים, שיתוף קישור, QR וספירות שימוש — דורשים אינטרנט.'}
                  </p>
                </div>
              </details>
              <div className="welcome-drawer-section">
                <span className="welcome-drawer-section-title">הוספת תמונה או דף חדש</span>
                <div className="welcome-source-grid">
                  <button type="button" className="welcome-source-btn" onClick={() => { setMainMenuOverlayOpen(false); void triggerMainImageUpload(); }}>
                    <span className="welcome-source-icon"><i className="fa-solid fa-upload" aria-hidden /></span>
                    <span className="welcome-source-label">העלאה מקובץ</span>
                  </button>
                  <button type="button" className="welcome-source-btn" onClick={() => { setMainMenuOverlayOpen(false); void openCamera(); }} title="צילום במצלמה">
                    <span className="welcome-source-icon"><i className="fa-solid fa-camera" aria-hidden /></span>
                    <span className="welcome-source-label">צילום</span>
                  </button>
                  {!childMode && (
                    <button type="button" className="welcome-source-btn" onClick={() => { pasteImageFromClipboard(); setMainMenuOverlayOpen(false); }}>
                      <span className="welcome-source-icon"><i className="fa-solid fa-paste" aria-hidden /></span>
                      <span className="welcome-source-label">הדבק תמונה</span>
                    </button>
                  )}
                </div>
              </div>
              <div className="welcome-drawer-section">
                <button type="button" className="welcome-drawer-btn" onClick={() => { startBlankPage(childMode ? { w: 800, h: 600 } : undefined); setMainMenuOverlayOpen(false); }}><i className="fa-solid fa-image" aria-hidden /> דף ריק</button>
                <button type="button" className="welcome-drawer-btn" onClick={() => { openGallery(); setMainMenuOverlayOpen(false); }}><i className="fa-solid fa-images" aria-hidden /> גלריה</button>
                <button type="button" className="welcome-drawer-btn" onClick={() => { setTemplatesGalleryOpen(true); setMainMenuOverlayOpen(false); }}><i className="fa-solid fa-shapes" aria-hidden /> תבניות לצביעה</button>
              </div>
              {childMode && (
                <div className="welcome-drawer-section">
                  <span className="welcome-drawer-section-title">{lang === 'en' ? 'Letter pages' : 'דף אותיות'}</span>
                  <button type="button" className="welcome-drawer-btn welcome-abc-one-page" onClick={() => { openAlphabetGridPage('hebrew'); setMainMenuOverlayOpen(false); }}>
                    <i className="fa-solid fa-font" aria-hidden /> {lang === 'en' ? 'Hebrew aleph-bet' : 'דף אלף-בית — כל האותיות'}
                  </button>
                  <button type="button" className="welcome-drawer-btn welcome-abc-one-page" onClick={() => { openAlphabetGridPage('latin'); setMainMenuOverlayOpen(false); }}>
                    <i className="fa-solid fa-font" aria-hidden /> {lang === 'en' ? 'English ABC' : 'דף ABC — אנגלית'}
                  </button>
                </div>
              )}
              {!childMode && (
                <>
                  <div className="welcome-drawer-section">
                    <span className="welcome-drawer-section-title">{lang === 'en' ? 'Build a word' : 'הרכיבי מילה'}</span>
                    <p className="welcome-compose-hint">{lang === 'en' ? 'Type a name or word — bubble letters for coloring.' : 'כתבי שם או מילה - יופיעו באותיות בלון יפות לצביעה.'}</p>
                    <div className="welcome-compose-row">
                      <input
                        type="text"
                        className="welcome-compose-input"
                        placeholder={lang === 'en' ? 'e.g. Hello' : 'למשל: השם שלי, שלום, אבא'}
                        value={composeWord}
                        onChange={(e) => setComposeWord(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && openWordPage()) setMainMenuOverlayOpen(false)
                        }}
                        dir="auto"
                      />
                      <button type="button" className="primary welcome-compose-btn" onClick={() => { if (openWordPage()) setMainMenuOverlayOpen(false) }}>
                        {lang === 'en' ? 'Create page' : 'צור דף צביעה'}
                      </button>
                    </div>
                  </div>
                  <div className="welcome-drawer-section">
                    <span className="welcome-drawer-section-title">{lang === 'en' ? 'Letters & numbers' : 'דפי צביעה מוכנים (אותיות ומספרים)'}</span>
                    <button type="button" className="welcome-drawer-btn welcome-abc-one-page" onClick={() => { openAlphabetGridPage('hebrew'); setMainMenuOverlayOpen(false); }}>
                      <i className="fa-solid fa-font" aria-hidden /> {lang === 'en' ? 'Hebrew aleph-bet (all letters)' : 'דף אלף-בית — כל האותיות העבריות'}
                    </button>
                    <button type="button" className="welcome-drawer-btn welcome-abc-one-page" onClick={() => { openAlphabetGridPage('latin'); setMainMenuOverlayOpen(false); }}>
                      <i className="fa-solid fa-font" aria-hidden /> {lang === 'en' ? 'English ABC (all letters)' : 'דף ABC — אותיות באנגלית'}
                    </button>
                    <div className="welcome-default-pages-grid welcome-drawer-chars">
                      {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((c) => (
                        <button key={c} type="button" className="welcome-default-char-btn" onClick={() => { openDefaultColoringPage(c); setMainMenuOverlayOpen(false); }}>{c}</button>
                      ))}
                    </div>
                    <div className="welcome-default-pages-grid welcome-drawer-chars">
                      {HEBREW_LETTERS.map((c) => (
                        <button key={c} type="button" className="welcome-default-char-btn" onClick={() => { openDefaultColoringPage(c); setMainMenuOverlayOpen(false); }}>{c}</button>
                      ))}
                    </div>
                    <div className="welcome-default-pages-grid welcome-drawer-chars">
                      {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map((c) => (
                        <button key={c} type="button" className="welcome-default-char-btn" onClick={() => { openDefaultColoringPage(c); setMainMenuOverlayOpen(false); }}>{c}</button>
                      ))}
                    </div>
                  </div>
                </>
              )}
              <div className="welcome-drawer-section">
                <button type="button" className="welcome-drawer-btn" onClick={() => { loadNewImage(); setMainMenuOverlayOpen(false); }}><i className="fa-solid fa-trash" aria-hidden /> תמונה חדשה (מחיקת ציור נוכחי)</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <header className="workspace-header">
        <div className="workspace-header-start">
          <button type="button" className="workspace-top-btn workspace-back-home" onClick={goToHome} title={lang === 'en' ? 'Home' : 'בית'} aria-label={lang === 'en' ? 'Home' : 'בית'}>
            <IconHome size={20} />
            <span>{lang === 'en' ? 'Home' : 'בית'}</span>
          </button>
          <button
            type="button"
            className="workspace-current-color workspace-current-color--header"
            title={lang === 'en' ? `${currentColor} — open color picker` : `${currentColor} — בוחר צבע`}
            aria-label={lang === 'en' ? `Open color picker, current ${currentColor}` : `פתיחת בוחר צבע, צבע נוכחי ${currentColor}`}
            onClick={() => headerColorInputRef.current?.click()}
          >
            <span
              className="workspace-current-color-swatch"
              style={{ backgroundColor: currentColor }}
              aria-hidden
            />
            <span className="workspace-current-color-hex">{currentColor}</span>
          </button>
          <input
            ref={headerColorInputRef}
            type="color"
            value={hexForNativeColorInput(currentColor)}
            onChange={(e) => setCurrentColor(e.target.value)}
            className="hidden-input"
            aria-hidden
            tabIndex={-1}
          />
        </div>
        <div className="workspace-header-actions">
          {childMode && (
            <button
              type="button"
              className="workspace-expand-mode-btn"
              onClick={() => setMode('companion')}
              title={lang === 'en' ? 'Full menu - all options' : 'תפריט מורכב - כל האפשרויות'}
              aria-label={lang === 'en' ? 'Switch to full menu' : 'מעבר לתפריט מורכב'}
            >
              <IconPlusSparkles size={22} />
              <span>{lang === 'en' ? 'Full menu' : 'תפריט מורכב'}</span>
            </button>
          )}
          <button
            type="button"
            className="workspace-top-btn workspace-name-btn"
            onClick={openNamePrompt}
            title={lang === 'en' ? 'Set your name (for shared room)' : 'שם להצגה בחדר ציור'}
            aria-label={lang === 'en' ? 'Set your name' : 'שם להצגה'}
          >
            <IconUser size={20} />
            {participantName ? <span className="workspace-name-btn-label">{participantName}</span> : null}
          </button>
          <button
            type="button"
            className="workspace-top-btn drawer-toggle"
            onClick={() => setMobileDrawerOpen((o) => !o)}
            aria-label={tr(lang, 'menu')}
            aria-expanded={mobileDrawerOpen}
            title={tr(lang, 'menu')}
          >
            <span className="drawer-toggle-icon"><IconMenu size={22} /></span>
          </button>
        </div>
      </header>

      <div className="workspace-body workspace-body--editor-layout">
        <div className="workspace-editor-row">
          <button
            type="button"
            className={`workspace-tools-backdrop ${toolsSideOpen ? 'is-visible' : ''}`}
            aria-label={lang === 'en' ? 'Close tools panel' : 'סגור פאנל כלים'}
            tabIndex={toolsSideOpen ? 0 : -1}
            onClick={() => setToolsSideOpen(false)}
          />
          <div
            className={`workspace-tools-shell ${toolsSideOpen ? 'is-open' : ''} ${disableDrawerPointerEvents ? 'no-pointer' : ''}`}
          >
            <button
              type="button"
              className="workspace-tools-edge-tab"
              aria-expanded={toolsSideOpen}
              aria-controls="drawer-panel-tools"
              onClick={() => setToolsSideOpen((v) => !v)}
            >
              {lang === 'en' ? 'Tools' : 'כלים'}
            </button>
            <div
              id="drawer-panel-tools"
              className="workspace-tools-panel drawer-panel-tools open"
              role="region"
              aria-hidden={!toolsSideOpen}
            >
              <div className="drawer-panel-inner workspace-tools-panel-inner">
                <MainTools
                  tool={tool}
                  setTool={(nextTool) => {
                    setTool(nextTool)
                  }}
                  expertMode={effectiveExpertMode}
                  lang={lang}
                  webPaintSplash={webPaintSplash}
                />
                {effectiveExpertMode && tool === 'flow' && (
                  <div className="flow-shape-picker" role="group" aria-label={lang === 'en' ? 'Flow chart tools' : 'כלי תרשים זרימה'}>
                    <div className="flow-shape-picker-label">{lang === 'en' ? 'Shapes — drag on canvas' : 'צורות — גרירה על הקנבס'}</div>
                    <div className="flow-shape-picker-btns">
                      {[
                        { id: 'arrow', he: 'חץ', en: 'Arrow' },
                        { id: 'line', he: 'קו', en: 'Line' },
                        { id: 'rect', he: 'מלבן', en: 'Rectangle' },
                        { id: 'roundRect', he: 'מעוגל', en: 'Rounded' },
                        { id: 'parallelogram', he: 'מקבילית', en: 'Parallelogram' },
                        { id: 'ellipse', he: 'אליפסה', en: 'Ellipse' },
                        { id: 'diamond', he: 'יהלום', en: 'Diamond' },
                        { id: 'hexagon', he: 'משושה', en: 'Hexagon' },
                      ].map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className={`drawer-grid-btn flow-shape-option ${flowShape === s.id ? 'active' : ''}`}
                          onClick={() => setFlowShape(s.id)}
                        >
                          {lang === 'en' ? s.en : s.he}
                        </button>
                      ))}
                    </div>
                    <div className="flow-shape-picker-label flow-shape-picker-label-sub">
                      {lang === 'en' ? 'Text & erase' : 'טקסט ומחיקה'}
                    </div>
                    <div className="flow-shape-picker-btns flow-shape-picker-btns-tools">
                      {[
                        { id: 'text', he: 'טקסט', en: 'Text' },
                        { id: 'erase', he: 'מחק', en: 'Erase' },
                      ].map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className={`drawer-grid-btn flow-shape-option ${flowShape === s.id ? 'active' : ''}`}
                          onClick={() => setFlowShape(s.id)}
                        >
                          {lang === 'en' ? s.en : s.he}
                        </button>
                      ))}
                    </div>
                    <p className="flow-shape-picker-hint">
                      {lang === 'en'
                        ? 'Text: tap (short click). Erase: drag like the eraser. Export includes the whole canvas.'
                        : 'טקסט: לחיצה קצרה. מחק: גרירה כמו מחק. הייצוא כולל את כל הקנבס.'}
                    </p>
                    <button
                      type="button"
                      className="drawer-grid-btn flow-shape-pdf-btn"
                      onClick={() => setFlowEditorModalOpen(true)}
                    >
                      {lang === 'en' ? 'Flow Pro editor…' : 'עורך Flow Pro…'}
                    </button>
                    <button
                      type="button"
                      className="drawer-grid-btn flow-shape-pdf-btn"
                      onClick={() => openExportModal()}
                    >
                      {lang === 'en' ? 'Export PDF / image…' : 'ייצוא PDF / תמונה…'}
                    </button>
                  </div>
                )}
                <section className="layers-panel" aria-label={lang === 'en' ? 'Layers' : 'שכבות'}>
                  <div className="layers-panel-header">
                    <strong>{lang === 'en' ? 'Layers' : 'שכבות'}</strong>
                    <button type="button" className="drawer-grid-btn" onClick={createLayer}>
                      {lang === 'en' ? '+ Add layer' : '+ הוסף שכבה'}
                    </button>
                  </div>
                  <div className="layers-panel-list">
                    {layers.map((layer, idx) => (
                      <div key={layer.id} className={`layers-panel-item ${activeLayerId === layer.id ? 'active' : ''}`}>
                        <button
                          type="button"
                          className="layers-panel-select"
                          onClick={() => switchActiveLayer(layer.id)}
                          title={lang === 'en' ? 'Set active layer' : 'הפוך לשכבה פעילה'}
                        >
                          {layer.name || `Layer ${idx + 1}`}
                        </button>
                        <button
                          type="button"
                          className={`layers-panel-toggle ${layer.visible ? 'is-visible' : 'is-hidden'}`}
                          onClick={() => toggleLayerVisibility(layer.id)}
                          title={layer.visible ? (lang === 'en' ? 'Hide layer' : 'הסתר שכבה') : (lang === 'en' ? 'Show layer' : 'הצג שכבה')}
                        >
                          {layer.visible ? (lang === 'en' ? 'Hide' : 'הסתר') : (lang === 'en' ? 'Show' : 'הצג')}
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
                {effectiveExpertMode && <ToolSizeControl value={brushSize} onChange={setBrushSize} />}
                {!childMode && (
                  <FunPanel
                    selectedSticker={selectedSticker}
                    onSelectSticker={(emoji) => {
                      setSelectedSticker(emoji)
                      setToolState(emoji ? 'sticker' : 'brush')
                    }}
                    onEffect={(type) => setEffectTrigger({ key: Date.now(), type })}
                    lang={lang}
                  />
                )}
                {adultMode && (
                  <section className="adult-pro-panel" aria-label="כלי מקצועיים">
                    <div className="adult-pro-panel-row">
                      <strong>מצב מקצועי: למבוגרים</strong>
                      <div className="adult-pro-presets">
                        <button
                          type="button"
                          className={`drawer-grid-btn ${adultPreset === 'tattoo' ? 'active' : ''}`}
                          onClick={() => applyAdultPreset('tattoo')}
                        >
                          Tattoo Designer
                        </button>
                        <button
                          type="button"
                          className={`drawer-grid-btn ${adultPreset === 'house' ? 'active' : ''}`}
                          onClick={() => applyAdultPreset('house')}
                        >
                          House Organizer
                        </button>
                      </div>
                    </div>

                    <div className="adult-pro-panel-row">
                      <label className="drawer-option-label">
                        <input
                          type="checkbox"
                          checked={lineAssistEnabled}
                          onChange={() => setLineAssistEnabled((v) => !v)}
                        />
                        <span>Local AI line assistant (offline)</span>
                      </label>
                      <input
                        type="range"
                        min="0.1"
                        max="0.92"
                        step="0.02"
                        value={lineAssistStrength}
                        onChange={(e) => setLineAssistStrength(Number(e.target.value))}
                      />
                    </div>

                    <div className="adult-pro-panel-row">
                      <strong>מדידה מתמונה</strong>
                      <span className="drawer-option-desc">בחרו כלי "מדידה", לחצו שתי נקודות, והגדירו כיול.</span>
                      <div className="adult-pro-presets">
                        <button
                          type="button"
                          className={`drawer-grid-btn ${tool === 'measure' ? 'active' : ''}`}
                          onClick={() => setTool('measure')}
                        >
                          כלי מדידה
                        </button>
                        <button
                          type="button"
                          className="drawer-grid-btn"
                          onClick={() => { void captureForMeasurement() }}
                        >
                          צלם למדידה
                        </button>
                      </div>
                      <div className="adult-measure-grid">
                        <label>
                          סמ ייחוס
                          <input type="number" min="0.1" step="0.1" value={measurementCalibrationCm} onChange={(e) => setMeasurementCalibrationCm(Number(e.target.value))} />
                        </label>
                        <label>
                          פיקסלים ייחוס
                          <input type="number" min="1" step="1" value={measurementCalibrationPx} onChange={(e) => setMeasurementCalibrationPx(Number(e.target.value))} />
                        </label>
                      </div>
                      <div className="adult-measure-result">
                        תוצאה: רוחב {measurementWidthCm.toFixed(2)} ס"מ ({measurementWidthInch.toFixed(2)}″) | גובה {measurementHeightCm.toFixed(2)} ס"מ ({measurementHeightInch.toFixed(2)}″) | אלכסון {measurementDistanceCm.toFixed(2)} ס"מ ({measurementDistanceInch.toFixed(2)}″)
                      </div>
                      <button type="button" className="drawer-grid-btn" onClick={() => setMeasurementPoints([])}>
                        אפס נקודות מדידה
                      </button>
                    </div>

                    <div className="adult-pro-panel-row">
                      <strong>הערות פרויקט</strong>
                      <textarea
                        className="adult-notes"
                        value={projectNotes}
                        onChange={(e) => setProjectNotes(e.target.value)}
                        placeholder="אפשר להוסיף מידות, חומרים, הערות לביצוע..."
                      />
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>

          <div className="workspace-canvas-column">
            <main className={`canvas-area canvas-area--workspace-frame ${splitMode ? 'split-mode' : ''}`}>
          {outlineSize.w > 0 && (
            <div className="canvas-measure-badge" role="status" title="מידות הקנבס">
              {outlineSize.w} × {outlineSize.h} px
            </div>
          )}
          {splitMode && (
            <div className="split-reference">
              <div className="split-reference-inner">
                {splitRefImageUrl ? (
                  <>
                    <img src={splitRefImageUrl} alt="תמונה להעתקה" className="split-reference-img" />
                    <div className="split-reference-actions">
                      <button type="button" className="split-ref-btn" onClick={() => void triggerSplitRefUpload()}>העלה תמונה</button>
                      <button type="button" className="split-ref-btn" onClick={pasteImageAsReference}>הדבק תמונה</button>
                      <button type="button" className="split-ref-btn" onClick={() => setSplitRefImageUrl(null)}>נקה</button>
                    </div>
                  </>
                ) : (
                  <div className="split-reference-placeholder">
                    <p>תמונה להעתקה</p>
                    <button type="button" className="primary split-ref-paste" onClick={pasteImageAsReference}>הדבק תמונה (Ctrl+V)</button>
                    <button type="button" className="split-ref-upload" onClick={() => void triggerSplitRefUpload()}>או העלה קובץ</button>
                  </div>
                )}
              </div>
              <input
                ref={splitRefFileInputRef}
                type="file"
                accept="image/*"
                className="hidden-input"
                onChange={(e) => {
                  const file = e?.target?.files?.[0]
                  if (file && isLikelyImageFile(file)) {
                    const r = new FileReader()
                    r.onload = () => setSplitRefImageUrl(r.result)
                    r.readAsDataURL(file)
                  }
                  e.target.value = ''
                }}
              />
            </div>
          )}
          {(outlineSize.w > 0 || isBlankPage) ? (
            <CanvasViewport
              zoom={canvasZoom}
              pan={canvasPan}
              setZoom={setCanvasZoom}
              setPan={setCanvasPan}
              lang={lang}
              lockViewport={false}
              contentSize={outlineSize}
            >
              <div className={splitMode ? 'split-drawing' : 'canvas-stack'}>
                <canvas
                  ref={layersUnderlayRef}
                  className="layers-underlay"
                  width={outlineSize.w}
                  height={outlineSize.h}
                  style={{ pointerEvents: 'none' }}
                  aria-hidden
                />
                <ColoringCanvas
                  ref={canvasRef}
                  outlineSize={outlineSize}
                  outlineDataRef={outlineMode === 'lines' ? outlineDataRef : null}
                  eraserBaseImageUrl={eraserBaseImageUrl}
                  currentColor={currentColor}
                  paintStyle={paintStyle}
                  brushSize={brushSize}
                  flowShape={flowShape}
                  tool={tool}
                  glitterIntensity={glitterIntensity}
                  stampChar={stampChar}
                  stampFontFamily={stampFontFamily}
                  stickerEmoji={tool === 'sticker' ? selectedSticker : null}
                  onUndoPush={pushUndo}
                  onStrokeEnd={onStrokeEnd}
                  lineAssistEnabled={lineAssistEnabled}
                  lineAssistStrength={lineAssistStrength}
                  onMeasurePoint={handleMeasurePoint}
                  splashToolEnabled={webPaintSplash}
                  onSplashFx={
                    webPaintSplash
                      ? (p) => setPaintSplashFx({ key: Date.now(), x: p.x, y: p.y, color: p.color })
                      : undefined
                  }
                  onPickColor={(hex) => {
                    setCurrentColor(hex)
                    setToolState('brush')
                    showToast(lang === 'en' ? 'Color picked' : 'נבחר צבע')
                  }}
                  onFlowTextRequest={requestFlowText}
                />
                {!isBlankPage && overlayImage && (
                  <canvas
                    ref={overlayCanvasRef}
                    className="outline-overlay"
                    width={outlineSize.w}
                    height={outlineSize.h}
                    style={{ pointerEvents: 'none' }}
                    aria-hidden
                  />
                )}
                {gridCells && outlineSize.w > 0 && outlineSize.h > 0 && (
                  <div
                    className="canvas-grid-overlay"
                    aria-hidden
                    style={{
                      width: outlineSize.w,
                      height: outlineSize.h,
                      pointerEvents: 'none',
                      backgroundImage: [
                        `linear-gradient(to right, rgba(0,0,0,0.35) 1px, transparent 1px)`,
                        `linear-gradient(to bottom, rgba(0,0,0,0.35) 1px, transparent 1px)`,
                      ].join(', '),
                      backgroundSize: `${outlineSize.w / gridCells.cols}px ${outlineSize.h / gridCells.rows}px`,
                    }}
                  />
                )}
                {triangleOverlay && outlineSize.w > 0 && outlineSize.h > 0 && (() => {
                  const w = outlineSize.w
                  const h = outlineSize.h
                  const step = Math.min(w, h) / 4
                  const lines = []
                  for (let y = 0; y <= h + 1; y += step) {
                    lines.push(<line key={`h-${y}`} x1={0} y1={y} x2={w} y2={y} stroke="rgba(0,0,0,0.35)" strokeWidth="1" />)
                  }
                  for (let i = -w; i <= h + w; i += step) {
                    lines.push(<line key={`d1-${i}`} x1={0} y1={i} x2={w} y2={i + w} stroke="rgba(0,0,0,0.35)" strokeWidth="1" />)
                    lines.push(<line key={`d2-${i}`} x1={w} y1={i} x2={0} y2={i + w} stroke="rgba(0,0,0,0.35)" strokeWidth="1" />)
                  }
                  return (
                    <svg
                      className="canvas-grid-overlay canvas-triangle-overlay"
                      width={w}
                      height={h}
                      viewBox={`0 0 ${w} ${h}`}
                      aria-hidden
                      style={{ pointerEvents: 'none' }}
                    >
                      {lines}
                    </svg>
                  )
                })()}
              </div>
            </CanvasViewport>
          ) : (
            <div className="canvas-placeholder" aria-hidden>
              {lang === 'en' ? 'Choose a template or start a blank page' : 'בחרו תבנית או התחילו דף ריק'}
            </div>
          )}
            </main>
          </div>
        </div>

        <div ref={bottomColorsRef} className={`workspace-bottom-colors-fixed ${disableDrawerPointerEvents ? 'no-pointer' : ''}`}>
          <div
            id="drawer-panel-colors"
            className={`drawer-panel-colors ${colorsPinned ? 'open' : ''}`}
            role="region"
            aria-hidden={!colorsPinned}
          >
            <div className="drawer-panel-inner">
              <SimpleColorPalette
                colors={colors}
                currentColor={currentColor}
                onSelect={(hex) => {
                  setCurrentColor(hex)
                }}
                onAddColor={addColor}
                onMixColors={mixColors}
                sensitivePress={sensitiveColorPress}
                easyMode={!effectiveExpertMode}
                lang={lang}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className="drawer-overlay"
        data-open={mobileDrawerOpen}
        onClick={closeDrawer}
        aria-hidden={!mobileDrawerOpen}
      />

      <div className="drawer-panel" data-open={mobileDrawerOpen}>
        <div className="drawer-panel-header">
          <h2>תפריט מורחב</h2>
          <button type="button" className="drawer-close" onClick={closeDrawer} aria-label="סגור תפריט">
            <IconClose size={22} />
          </button>
        </div>
        <div className="drawer-panel-body">
          {!childMode && (
            <div className="drawer-mode-toggles">
              <button
                type="button"
                className={`drawer-mode-btn ${splitMode ? 'active' : ''}`}
                onClick={toggleSplitMode}
                aria-pressed={splitMode}
              >
                {splitMode ? 'סגור פיצול' : 'מצב פיצול'}
              </button>
              <button
                type="button"
                className={`drawer-mode-btn expert ${effectiveExpertMode ? 'active' : ''}`}
                onClick={toggleExpertMode}
                aria-pressed={effectiveExpertMode}
              >
                {effectiveExpertMode ? 'מצב קל' : 'מצב מומחה'}
              </button>
            </div>
          )}
          {childMode && (
            <div className="drawer-grid-section drawer-ready-pages" role="group" aria-label={lang === 'en' ? 'Letter pages' : 'דפי אותיות'}>
              <span className="drawer-grid-label">{lang === 'en' ? 'Letter pages' : 'דף אותיות'}</span>
              <button type="button" className="welcome-drawer-btn welcome-abc-one-page drawer-ready-page-btn" onClick={() => { openAlphabetGridPage('hebrew'); closeDrawer(); }}>
                <i className="fa-solid fa-font" aria-hidden /> {lang === 'en' ? 'Hebrew aleph-bet' : 'דף אלף-בית — כל האותיות'}
              </button>
              <button type="button" className="welcome-drawer-btn welcome-abc-one-page drawer-ready-page-btn" onClick={() => { openAlphabetGridPage('latin'); closeDrawer(); }}>
                <i className="fa-solid fa-font" aria-hidden /> {lang === 'en' ? 'English ABC' : 'דף ABC — אנגלית'}
              </button>
            </div>
          )}
          {!childMode && (
            <>
              <div className="drawer-grid-section drawer-ready-pages" role="group" aria-label={lang === 'en' ? 'Build a word' : 'הרכיבי מילה'}>
                <span className="drawer-grid-label">{lang === 'en' ? 'Build a word' : 'הרכיבי מילה'}</span>
                <p className="drawer-option-desc drawer-ready-pages-hint">
                  {lang === 'en' ? 'Type a name or word — bubble letters for coloring.' : 'כתבי שם או מילה — יופיעו באותיות בלון לצביעה.'}
                </p>
                <div className="welcome-compose-row drawer-compose-row">
                  <input
                    type="text"
                    className="welcome-compose-input"
                    placeholder={lang === 'en' ? 'e.g. Hello' : 'למשל: השם שלי, שלום'}
                    value={composeWord}
                    onChange={(e) => setComposeWord(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && openWordPage()) closeDrawer()
                    }}
                    dir="auto"
                  />
                  <button type="button" className="primary welcome-compose-btn" onClick={() => { if (openWordPage()) closeDrawer() }}>
                    {lang === 'en' ? 'Create page' : 'צור דף צביעה'}
                  </button>
                </div>
              </div>
              <div className="drawer-grid-section drawer-ready-pages" role="group" aria-label={lang === 'en' ? 'Letters and numbers' : 'דפי צביעה מוכנים'}>
                <span className="drawer-grid-label">{lang === 'en' ? 'Letters & numbers' : 'דפי צביעה מוכנים (אותיות ומספרים)'}</span>
                <button type="button" className="welcome-drawer-btn welcome-abc-one-page drawer-ready-page-btn" onClick={() => { openAlphabetGridPage('hebrew'); closeDrawer(); }}>
                  <i className="fa-solid fa-font" aria-hidden /> {lang === 'en' ? 'Hebrew aleph-bet (all letters)' : 'דף אלף-בית — כל האותיות העבריות'}
                </button>
                <button type="button" className="welcome-drawer-btn welcome-abc-one-page drawer-ready-page-btn" onClick={() => { openAlphabetGridPage('latin'); closeDrawer(); }}>
                  <i className="fa-solid fa-font" aria-hidden /> {lang === 'en' ? 'English ABC (all letters)' : 'דף ABC — אותיות באנגלית'}
                </button>
                <div className="welcome-default-pages-grid welcome-drawer-chars">
                  {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((c) => (
                    <button key={c} type="button" className="welcome-default-char-btn" onClick={() => { openDefaultColoringPage(c); closeDrawer(); }}>{c}</button>
                  ))}
                </div>
                <div className="welcome-default-pages-grid welcome-drawer-chars">
                  {HEBREW_LETTERS.map((c) => (
                    <button key={c} type="button" className="welcome-default-char-btn" onClick={() => { openDefaultColoringPage(c); closeDrawer(); }}>{c}</button>
                  ))}
                </div>
                <div className="welcome-default-pages-grid welcome-drawer-chars">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map((c) => (
                    <button key={c} type="button" className="welcome-default-char-btn" onClick={() => { openDefaultColoringPage(c); closeDrawer(); }}>{c}</button>
                  ))}
                </div>
              </div>
            </>
          )}
          {(outlineSize.w > 0 || isBlankPage) && (
            <div className="drawer-grid-section" role="group" aria-label={lang === 'en' ? 'Grid overlay' : 'משבצות ומשולשים על הדף'}>
              <span className="drawer-grid-label">{lang === 'en' ? 'Grid / triangles' : 'משבצות / משולשים'}</span>
              <div className="drawer-grid-btns">
                <button
                  type="button"
                  className={`drawer-grid-btn ${!gridCells && !triangleOverlay ? 'active' : ''}`}
                  onClick={() => { setGridCells(null); setTriangleOverlay(false); }}
                  aria-pressed={!gridCells && !triangleOverlay}
                >
                  {lang === 'en' ? 'None' : 'ללא'}
                </button>
                {[2, 3, 4].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`drawer-grid-btn ${gridCells?.rows === n && gridCells?.cols === n ? 'active' : ''}`}
                    onClick={() => { setGridCells({ rows: n, cols: n }); setTriangleOverlay(false); }}
                    aria-pressed={gridCells?.rows === n && gridCells?.cols === n}
                  >
                    {n}×{n}
                  </button>
                ))}
                <button
                  type="button"
                  className={`drawer-grid-btn ${triangleOverlay ? 'active' : ''}`}
                  onClick={() => { setGridCells(null); setTriangleOverlay(true); }}
                  aria-pressed={triangleOverlay}
                >
                  {lang === 'en' ? 'Triangles' : 'משולשים'}
                </button>
              </div>
            </div>
          )}
          {(outlineSize.w > 0 || isBlankPage) && (
            <div className="drawer-grid-section" role="group" aria-label={lang === 'en' ? 'Page background' : 'רקע הדף'}>
              <span className="drawer-grid-label">{lang === 'en' ? 'Page background' : 'רקע הדף'}</span>
              {effectiveExpertMode && !childMode && (
                <div className="drawer-paper-threshold">
                  <label htmlFor="paper-fill-threshold" className="drawer-paper-threshold-label">
                    {lang === 'en' ? 'How much to treat as empty paper (slide)' : 'כמה בהיר נחשב "דף ריק" — החליקו'}
                  </label>
                  <div className="drawer-paper-threshold-row">
                    <input
                      id="paper-fill-threshold"
                      type="range"
                      min={220}
                      max={255}
                      value={paperFillThreshold}
                      onChange={(e) => setPaperFillThreshold(Number(e.target.value))}
                      aria-valuemin={220}
                      aria-valuemax={255}
                      aria-valuenow={paperFillThreshold}
                    />
                    <span className="drawer-paper-threshold-value">{paperFillThreshold}</span>
                  </div>
                </div>
              )}
              <p className="drawer-option-desc drawer-paper-fill-hint">
                {lang === 'en'
                  ? 'Fills bright “paper” pixels with the color you picked (lines stay). Undo if needed.'
                  : 'ממלא את כל שטח הדף הבהיר בצבע שנבחר בלוח; קווי המתאר נשמרים. אפשר לבטל במידת הצורך.'}
              </p>
              <button
                type="button"
                className="primary drawer-paper-fill-btn"
                onClick={() => { fillPaperBackground(); setMobileDrawerOpen(false); }}
              >
                {lang === 'en' ? 'Fill entire background' : 'מלא את כל הרקע בצבע הנבחר'}
              </button>
            </div>
          )}
          {(outlineSize.w > 0 || isBlankPage) && (
            <div className="drawer-grid-section" role="group" aria-label={lang === 'en' ? 'Outline mode' : 'קווי מתאר'}>
              <span className="drawer-grid-label">{lang === 'en' ? 'Outline' : 'קווי מתאר'}</span>
              <div className="drawer-grid-btns">
                <button
                  type="button"
                  className={`drawer-grid-btn ${outlineMode === 'lines' ? 'active' : ''}`}
                  onClick={() => setOutlineMode('lines')}
                  aria-pressed={outlineMode === 'lines'}
                >
                  {lang === 'en' ? 'Separate lines (black)' : 'שמור קווי מתאר (שחור)'}
                </button>
                <button
                  type="button"
                  className={`drawer-grid-btn ${outlineMode === 'drawing' ? 'active' : ''}`}
                  onClick={() => setOutlineMode('drawing')}
                  aria-pressed={outlineMode === 'drawing'}
                >
                  {lang === 'en' ? 'Make lines part of drawing' : 'הפוך לחלק מהציור'}
                </button>
              </div>
            </div>
          )}
          {effectiveExpertMode && (outlineSize.w > 0 || isBlankPage) && (
            <div className="drawer-grid-section" role="group" aria-label={lang === 'en' ? 'Outline quality' : 'איכות קווי מתאר'}>
              <span className="drawer-grid-label">{lang === 'en' ? 'Outline quality' : 'איכות קווי מתאר'}</span>
              <div className="drawer-grid-btns">
                {[
                  { id: 'perfect', label: lang === 'en' ? 'Perfect' : 'מושלם' },
                  { id: 'clean', label: lang === 'en' ? 'Clean lines' : 'קווים נקיים' },
                  {
                    id: 'mandala',
                    label: lang === 'en' ? 'Mandala' : 'מנדלה',
                    title: lang === 'en'
                      ? 'Kaleidoscope symmetry from the photo — mandala-style coloring'
                      : 'סימטריית קליידוסקופ מהתמונה — דף צביעה בסגנון מנדלה',
                  },
                  { id: 'sharp', label: lang === 'en' ? 'Sharp' : 'חד' },
                  {
                    id: 'upscale',
                    label: lang === 'en' ? 'HD photo' : 'צילום HD',
                    title: lang === 'en'
                      ? 'Upscale photo before converting to coloring lines'
                      : 'הגדלת צילום לפני המרה לקווי צביעה',
                  },
                  { id: 'standard', label: lang === 'en' ? 'Standard' : 'רגיל' },
                  { id: 'soft', label: lang === 'en' ? 'Soft' : 'רך' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`drawer-grid-btn ${outlineQuality === opt.id ? 'active' : ''}`}
                    onClick={() => setOutlineQuality(opt.id)}
                    aria-pressed={outlineQuality === opt.id}
                    title={opt.title ?? (lang === 'en' ? 'Affects how photos convert to outlines' : 'משפיע על המרת תמונות לקווי מתאר')}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="drawer-option-desc" style={{ marginTop: '0.5rem' }}>
                {lang === 'en' ? 'Applies to the next photo conversion.' : 'חל על המרה הבאה של תמונה.'}
              </p>
            </div>
          )}
          {outlineSize.w > 0 && (
            <div className="drawer-canvas-measure" role="status">
              <span className="drawer-canvas-measure-label">מידות קנבס</span>
              <span className="drawer-canvas-measure-value">{outlineSize.w} × {outlineSize.h} px</span>
            </div>
          )}
          <details className="drawer-room-block">
            <summary><strong>{lang === 'en' ? 'Settings' : 'הגדרות'}</strong></summary>
            <div className="drawer-option-row" style={{ marginTop: '0.75rem' }}>
              <label className="drawer-option-label">
                <input type="checkbox" checked={darkMode} onChange={() => setDarkMode((v) => !v)} />
                <span>{lang === 'en' ? 'Dark mode' : 'מצב כהה'}</span>
              </label>
            </div>
            <div className="drawer-option-row">
              <label className="drawer-option-label">
                <input type="checkbox" checked={lang === 'en'} onChange={toggleLang} />
                <span>{lang === 'en' ? 'English' : 'אנגלית'}</span>
              </label>
            </div>
            <div className="drawer-option-row">
              <label className="drawer-option-label">
                <input type="checkbox" checked={readAloudMode} onChange={toggleReadAloud} />
                <span>{lang === 'en' ? 'Read aloud' : 'הקראה'}</span>
              </label>
            </div>
            <div className="drawer-option-row">
              <label className="drawer-option-label">
                <input type="checkbox" checked={accessibilityMode} onChange={toggleAccessibility} />
                <span>{lang === 'en' ? 'Accessibility' : 'נגישות'}</span>
              </label>
            </div>
            <div className="drawer-option-row">
              <label className="drawer-option-label">
                <input
                  type="checkbox"
                  checked={offlineStrictMode}
                  onChange={() => setOfflineStrictMode((v) => !v)}
                />
                <span>{lang === 'en' ? 'Strict offline (no network)' : 'מצב אופליין מחמיר (בלי רשת)'}</span>
              </label>
              <p className="drawer-option-desc">
                {lang === 'en'
                  ? 'Off: image search, shared rooms, share link, QR, and analytics may use the network.'
                  : 'כבוי: חיפוש תמונות, חדרים משותפים, שיתוף קישור, QR וספירות שימוש — דורשים אינטרנט.'}
              </p>
            </div>
          </details>
          <div className="drawer-option-row">
            <label className="drawer-option-label">
              <input
                type="checkbox"
                checked={sensitiveColorPress}
                onChange={(e) => setSensitiveColorPress(e.target.checked)}
                aria-describedby="sensitive-color-desc"
              />
              <span>לחיצה רגישה על צבעים</span>
            </label>
            <p id="sensitive-color-desc" className="drawer-option-desc">בחירת צבע מיד במגע, אזור לחיצה גדול יותר</p>
          </div>
          {!childMode && (
            <div className="drawer-option-row">
              <button type="button" className="drawer-admin-link" onClick={() => { setAdminOpen(true); setMobileDrawerOpen(false); }}>
                ניהול קטגוריות – הוספת תמונות ותבניות
              </button>
            </div>
          )}
          {roomId && roomInviteLink && (
            <div className="drawer-room-block">
              <strong>חדר ציור פעיל</strong>
              <p className="drawer-room-hint">הציור נשמר אוטומטית. שלחו את הקישור להזמנת חברים.</p>
              {roomSyncIssue && <p className="drawer-room-hint">{roomSyncIssue}</p>}
              <div className="drawer-room-name-row">
                <label htmlFor="room-participant-name-drawer">שם משתתף</label>
                <input id="room-participant-name-drawer" type="text" className="drawer-room-name-input" placeholder="השם שיוצג בעדכונים" value={participantName || ''} onChange={(e) => setParticipantName(e.target.value)} />
              </div>
              <div className="drawer-room-link-row">
                <input type="text" className="drawer-room-input" readOnly value={roomInviteLink} />
                <button type="button" className="drawer-room-copy" onClick={copyRoomLink}>העתק</button>
              </div>
            </div>
          )}
          <MenuPanel
            expertMode={effectiveExpertMode}
            brushSize={brushSize}
            setBrushSize={setBrushSize}
            glitterIntensity={glitterIntensity}
            setGlitterIntensity={setGlitterIntensity}
            onUndo={undo}
            undoDisabled={undoStack.length === 0}
            onClear={clearColors}
            onOpenExportModal={openExportModal}
            onFilterPicture={openExportModal}
            onToggleVideoRecording={toggleVideoRecording}
            isVideoRecording={videoRecording}
            onNewImage={loadNewImage}
            onPasteImage={pasteImageFromClipboard}
            onSaveToGallery={saveToGallery}
            onTakeScreenshot={takeScreenshot}
            onPhotoToPainting={triggerPhotoToPaintingUpload}
            onRemoveBackground={removeBackground}
            onResizePicture={resizePicture}
            onOpenGallery={openGallery}
            tool={tool}
            setTool={setTool}
            onSelectStamp={setStampChar}
            stampFontFamily={stampFontFamily}
            onSelectStampFont={setStampFontFamily}
            onShareDrawing={shareDrawing}
            onShareApp={shareApp}
            lang={lang}
          >
            <PaintStyleBar paintStyle={paintStyle} setPaintStyle={setPaintStyle} />
          </MenuPanel>
          {effectiveExpertMode && (
            <ColorPalette
              colors={colors}
              currentColor={currentColor}
              onSelect={setCurrentColor}
              onAddColor={addColor}
              onRemoveColor={removeColor}
              onMixColors={mixColors}
              sensitivePress={sensitiveColorPress}
              lang={lang}
            />
          )}
        </div>
      </div>

      {galleryOpen && (
        <Gallery
          items={galleryItems}
          onClose={() => setGalleryOpen(false)}
          onItemsChange={() => setGalleryItems(getGallery())}
          onOpenForEdit={(item) => loadCompositeIntoCanvas(item.dataUrl)}
        />
      )}

      {effectTrigger && (
        <div className="effect-overlay" aria-hidden>
          {effectTrigger.type === 'confetti' && (
            <ConfettiBurst key={effectTrigger.key} onDone={() => setEffectTrigger(null)} />
          )}
          {effectTrigger.type === 'sparkle' && (
            <SparkleBurst key={effectTrigger.key} onDone={() => setEffectTrigger(null)} />
          )}
        </div>
      )}
      {paintSplashFx && (
        <PaintSplashBurst
          key={paintSplashFx.key}
          x={paintSplashFx.x}
          y={paintSplashFx.y}
          color={paintSplashFx.color}
          onDone={() => setPaintSplashFx(null)}
        />
      )}
      {liveUpdateFrom && (
        <div className="live-update-toast live-update-toast-bottom" role="status" aria-live="polite">
          <span className="live-update-toast-text">{liveUpdateFrom} עדכן/ה את הציור</span>
        </div>
      )}
      {videoRecording && (
        <button
          type="button"
          className="video-stop-fab"
          onClick={stopVideoRecording}
          aria-label={lang === 'en' ? 'Stop recording and save video' : 'עצור הקלטה ושמור וידאו'}
          title={lang === 'en' ? 'Stop recording and save video' : 'עצור הקלטה ושמור וידאו'}
        >
          {lang === 'en' ? '⏹ Stop & save' : '⏹ עצור ושמור'}
        </button>
      )}
      {roomId && roomLiveEvents.length > 0 && (
        <div className="room-live-bar" role="region" aria-label="עדכונים חיים מהחדר">
          <details className="room-live-details">
            <summary className="room-live-summary">עדכונים חיים ({roomLiveEvents.length})</summary>
            <ul className="room-live-list">
              {roomLiveEvents.slice(0, 12).map((e) => (
                <li key={e.id} className="room-live-item">
                  <span className="room-live-who">{e.participantName}</span>
                  <span className="room-live-when">{formatEventTime(e.created_at)}</span>
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}
      {outlineSize.w > 0 && (
        <div className="undo-redo-bar" role="group" aria-label="בטל / בצע שוב">
          <button
            type="button"
            className="undo-redo-btn"
            onClick={undo}
            disabled={undoStack.length === 0}
            title="בטל"
            aria-label="בטל"
          >
            <IconUndo size={22} />
          </button>
          <button
            type="button"
            className="undo-redo-btn"
            onClick={redo}
            disabled={redoStack.length === 0}
            title="בצע שוב"
            aria-label="בצע שוב"
          >
            <IconRedo size={22} />
          </button>
        </div>
      )}
      <footer className="app-footer">
        {uniqueDeviceCount != null && (
          <div className="app-footer-audit" title="מכשירים ייחודיים שהשתמשו באפליקציה">
            <span className="app-footer-audit-icon"><IconEye size={16} /></span>
            <span className="app-footer-audit-count">{uniqueDeviceCount}</span>
          </div>
        )}
        <button
          type="button"
          className="app-footer-about-toggle"
          onClick={toggleAbout}
          aria-expanded={aboutOpen}
        >
          אודות ועוד
        </button>
        {aboutOpen && (
          <div className="app-footer-about-content">
            <a
              href={`${import.meta.env.BASE_URL || '/'}landing.html`}
              className="app-footer-site-link"
            >
              דף הבית - אודות הפרויקט (הסבר מלא)
            </a>
            {!offlineStrictMode && (
              <a
                href="https://play.google.com/store/apps/details?id=com.colordev.colorphoto&hl=he"
                target="_blank"
                rel="noreferrer"
                className="app-footer-site-link"
              >
                הורידו את האפליקציה מ-Google Play
              </a>
            )}
            <span className="app-footer-brand">BY COLORDEV.ART</span>
            <span className="app-footer-version" title="גרסת האתר">
              {lang === 'en' ? 'Web version' : 'גרסת אתר'} {import.meta.env.VITE_APP_VERSION ?? '—'}
            </span>
            {!offlineStrictMode && (
              <a
                href="https://cards.colordev.art"
                target="_blank"
                rel="noreferrer"
                className="app-footer-site-link"
              >
                האתר שלנו - cards.colordev.art
              </a>
            )}
            <div className="app-footer-story">
              <strong>איך נוצרה האפליקציה</strong>
              <p>אמא לילדים חשבה כמה נוח יהיה לצלם משהו שהילד רוצה לצייר - והתמונה תהפוך מיד לדף צביעה בחוברת. כך נולדה האפליקציה: צילום מהיר, קווי מתאר, צביעה.</p>
            </div>
            <div className="app-footer-guide">
              <strong>מדריך להורה</strong>
              <ul>
                <li>העלו תמונה מהמכשיר או צלמו ישירות. במצב אופליין אין חיפוש אינטרנט.</li>
                <li>בחרו סגנון צביעה (מט, זהב, ניאון וכו') וצבעו עם המברשת או המילוי.</li>
                <li>אפשר לשתף את הציור או לייצא ל-PDF לדפוס.</li>
                <li><strong>חדר ציור משותף:</strong> מאובטח לציור משותף עם חברים - דרוש הורה משגיח.</li>
              </ul>
            </div>
            {!offlineStrictMode && (
              <>
                <button
                  type="button"
                  className="app-footer-share-qr-btn"
                  onClick={() => setQrModalOpen(true)}
                  aria-label="שתף - הצג קוד QR"
                >
                  שתף
                </button>
                <a
                  href="https://www.paypal.com/donate/?hosted_button_id=QPPDT97GAMX58"
                  target="_blank"
                  rel="noreferrer"
                  className="app-footer-donate"
                >
                  תרומה <span className="app-footer-heart">❤</span>
                </a>
              </>
            )}
            <button
              type="button"
              className="app-footer-admin-btn"
              onClick={() => { setAdminOpen(true); setAboutOpen(false); }}
              aria-label="ניהול קטגוריות"
            >
              ניהול קטגוריות
            </button>
            <div className="app-footer-disclaimer-box">
              <strong className="app-footer-disclaimer-title">גילוי נאות</strong>
              <p>המפתח והמפיץ של האפליקציה פטורים מכל אחריות. השימוש באפליקציה ובחדר הציור המשותף - כולו על אחריות ההורה או האפוטרופוס המשגיח בלבד.</p>
              <p>האפליקציה פועלת בדפדפן; תמונות לא נשמרות בשרת שלנו. התרומות וולונטריות ומעובדות דרך PayPal.</p>
            </div>
          </div>
        )}
      </footer>
      {adminOpen && (
        <Suspense fallback={null}>
          <AdminCategories onClose={() => setAdminOpen(false)} />
        </Suspense>
      )}
      {!offlineStrictMode && qrModalOpen && (
        <div className="qr-modal-overlay" role="dialog" aria-modal="true" aria-label="קוד QR לשיתוף" onClick={() => setQrModalOpen(false)}>
          <div className="qr-modal-box" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="qr-modal-close" onClick={() => setQrModalOpen(false)} aria-label="סגור">
              <IconClose size={20} />
            </button>
            <p className="qr-modal-title">סרוק לשתף</p>
            <img
              className="qr-modal-image"
              src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(getPublicAppBaseUrl())}`}
              alt="קוד QR לפתיחת האפליקציה"
              width={140}
              height={140}
            />
          </div>
        </div>
      )}
      {templatesGalleryOpen && (
        <Suspense fallback={null}>
          <TemplatesGallery
            onSelect={applyTemplate}
            onClose={() => setTemplatesGalleryOpen(false)}
            onSelectCustomImage={(url) => { void openTemplateImageAsColoringPage(url) }}
            childMode={childMode}
          />
        </Suspense>
      )}
      {imageSearchModalOpen && (
        <div
          className="image-search-modal-overlay"
          onClick={() => setImageSearchModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="תוצאות חיפוש תמונות"
        >
          <div className="image-search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="image-search-modal-header">
              <div className="image-search-modal-title">בחרו תמונה לצביעה</div>
              <button type="button" className="image-search-modal-close" onClick={() => setImageSearchModalOpen(false)} aria-label="סגור תפריט">
                <IconClose size={22} />
              </button>
            </div>
            {imageSearchError && <div className="welcome-search-error">{imageSearchError}</div>}
            <div className="image-search-modal-grid" role="list">
              {imageSearchResults.map((r) => (
                <button
                  key={r.fullUrl}
                  type="button"
                  className="image-search-modal-item"
                  onClick={async () => {
                    setImageSearchModalOpen(false)
                    await importImageFromUrl(r.fullUrl, r.title)
                  }}
                  title={`ייבוא: ${r.title}`}
                  aria-label={`ייבוא תמונה: ${r.title}`}
                >
                  <img src={r.thumbUrl} alt={r.title} loading="lazy" />
                </button>
              ))}
            </div>
            <div className="image-search-modal-hint">בחרו תמונה - בלחיצה היא תתווסף לאפליקציה לצביעה.</div>
            <div className="image-search-modal-google">
              <button type="button" className="welcome-search-secondary-btn" onClick={() => openGoogleImageSearch(imageSearchQuery)}>
                חפש גם בגוגל תמונות
              </button>
            </div>
          </div>
        </div>
      )}
      {geoBuilderOpen && (
        <GeometricBuilder
          onCreate={applyOutlineCanvas}
          onClose={() => setGeoBuilderOpen(false)}
        />
      )}
      
      {exportModalOpen && getCompositeCanvas() && (
        <ExportModal
          getCompositeCanvas={getCompositeCanvas}
          onApplyToCanvas={applyFilterToPicture}
          onClose={() => setExportModalOpen(false)}
        />
      )}
      {flowEditorModalOpen && (
        <FlowEditorModal
          onCreate={applyFlowEditorCanvas}
          onClose={() => setFlowEditorModalOpen(false)}
          lang={lang}
        />
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  )
}
